/**
 * Order planning — /app/planning, under OPS. Design doc:
 * docs/plans/2026-08-24-order-planning-design.md. Two live sections —
 * Reorder recommendations (module-gated on 'amazon') and the active draft
 * plan (never module-gated: a VA curating a plan doesn't need Amazon sync
 * on) — plus a ComingRow-style placeholder for the future Approvals rail
 * (docs/plans/2026-08-24-order-planning-design.md §"Approvals lifecycle").
 *
 * Layout (live-feedback follow-up, 2026-08-24 afternoon): constrained to
 * max-w-6xl — the earlier full-bleed layout sprawled past a readable line
 * length at wide viewports. The Reorder table carries a filter-pill bar
 * (default "Needs attention" = reorder_now + watch) and a per-row exclusion
 * toggle (listing_map.no_reorder) that removes a SKU from computed
 * recommendations entirely — it shows under the Excluded pill instead.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Minus,
  RefreshCw,
  Search,
  Trash2,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import type {
  ApiError,
  BuyingSettings,
  OrderImportResponse,
  OrderPlan,
  OrderPlanLine,
  OrderPlanLineResearchStep,
  OrderPlanLineStatus,
  OrderPlanRecommendation,
  OrderPlanStatus,
  ProductSearchResult,
  PlanningCandidate,
  PlanningRun,
} from '@sourcito/schemas';
import { AppShell } from '../../components/AppShell';
import { OrderImportModal } from '../../components/OrderImportModal';
import { ProductThumb } from '../../components/ProductThumb';
import { ProductLink } from '../../components/ProductLink';
import { Skeleton, SkeletonLine, SkeletonRows } from '../../components/Skeleton';
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '../../design';
import { useMe, useModuleEnabled } from '../../lib/auth';
import { ApiCallError } from '../../lib/api';
import { handlingRiskLabel } from '../../lib/bands';
import { formatRelativeTime } from '../../lib/finance-summary';
import { shortDateLabel } from '../../lib/shipments';
import {
  authorizationBadge,
  useBuyingSettings,
  useUpdateBuyingSettings,
  parseBuyingSettingsInputs,
  type AsinUsage,
  buildMathCard,
  compareRecommendations,
  createOrderPlanLine,
  DEFAULT_PLAN_LINE_FILTER,
  DEFAULT_REORDER_FILTER,
  filterPlanLines,
  filterRecommendations,
  LINE_TOTAL_NOT_COMPUTED_NOTE,
  lookupAsinUsage,
  money,
  planAsinUsage,
  parseDollarInput,
  parseIntendedPriceInput,
  planHeaderSummary,
  planLineFilterCounts,
  planLineHoldBadgeLabel,
  planningHeldLinesLabel,
  planLineLeadSummary,
  planLinePerUnitLabel,
  planLineToMathCardInput,
  PLAN_LINE_FILTER_PILLS,
  type PlanLineFilterKey,
  productSearchResultToLineDefaults,
  shouldAutoResearchNewLine,
  manualLineCostCentsToSend,
  recommendationBadge,
  REORDER_FILTER_PILLS,
  reorderFilterCounts,
  researchInvalidatedByPriceChange,
  researchProgressLabels,
  researchStepFailures,
  researchVerdictSentence,
  type ReorderFilterKey,
  searchPlanLines,
  sparklinePath,
  useCreateOrderPlan,
  useCreateOrderPlanLine,
  useDeleteOrderPlanLine,
  useDeleteOrderPlan,
  useOrderPlan,
  planLineProfitEstimate,
  planSummaryMarginPct,
  percent,
  planLineDiscountScenarios,
  planLineDiscountNeeded,
  planLineDiscountNeededLabel,
  discountViableTitle,
  velocityChip,
  useOrderPlanRecommendations,
  useOrderPlanKeepaBudget,
  useLatestPlanningRun,
  useStartPlanningRunNow,
  usePlanningCandidates,
  useOrderPlans,
  useProductSearch,
  useResearchOrderPlanLine,
  useBulkResearchPlan,
  useSetReorderExclusion,
  useUpdateOrderPlan,
  useUpdateOrderPlanLine,
  usePutPackOverride,
  usePutSellableOverride,
  useDeleteSellableOverride,
  sellableOverrideChip,
  verdictTierBadge,
  keepaBudgetLabel,
  keepaOutOfTokens,
  keepaRetryCountdownLabel,
  keepaSecondsUntilAvailable,
} from '../../lib/order-planning';
import { MathCard } from './MathCard';

/** ApiCallError.details is the raw response body — pull the error code out
 *  of it when present, to special-case a message beyond the server's own
 *  (e.g. RESEARCH_STALE renders a shorter, action-oriented prompt). */
function errorCode(err: unknown): string | null {
  if (!(err instanceof ApiCallError)) return null;
  const details = err.details as Partial<ApiError> | undefined;
  return details?.error?.code ?? null;
}

/** KEEPA_NO_TOKENS's retryInSeconds hint, pulled from the same raw error
 *  body errorCode() reads (Phase A.1, 2026-08-31 planning-page-usability
 *  design). null when absent (any other error code). */
function retryInSecondsFromError(err: unknown): number | null {
  if (!(err instanceof ApiCallError)) return null;
  const details = err.details as { error?: { details?: { retryInSeconds?: number } } } | undefined;
  return details?.error?.details?.retryInSeconds ?? null;
}

/**
 * The per-listing inputs behind a proposal's verdict — price, Amazon fee, unit
 * cost, prep and allocated freight.
 *
 * Shown as a breakdown, deliberately NOT as an equation summing to profit:
 * profit is computed per proposal over its whole quantity, so a per-listing
 * `a - b - c = profit` line would be an identity the data does not always
 * satisfy. Faraz, 2026-09-05: prep in particular has to be visible — it moved
 * every candidate's verdict while being invisible in the UI.
 */
function PerListingMath({ proposal }: { proposal: NonNullable<PlanningCandidate['decision']> }) {
  const costPerListing =
    proposal.vendorUnitCostCents === null
      ? null
      : proposal.vendorUnitCostCents * proposal.vendorUnitsPerListing;
  const parts: string[] = [];
  const push = (label: string, cents: number | null) => {
    if (cents !== null) parts.push(`${money(cents)} ${label}`);
  };
  push('price', proposal.pricePerListingCents);
  push('Amazon fee', proposal.liveFeePerListingCents);
  push('cost', costPerListing);
  push('prep', proposal.prepPerListingCents);
  if (parts.length === 0) return null;
  return <span className="w-full text-xs text-fg-tertiary tabular-nums">{parts.join(' · ')}</span>;
}

export function PlanningPage() {
  const plansQuery = useOrderPlans();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const planId = selectedPlanId ?? plansQuery.data?.[0]?.id ?? null;
  const planQuery = useOrderPlan(planId);
  const createPlan = useCreateOrderPlan();
  const [error, setError] = useState<string | null>(null);
  const amazonEnabled = useModuleEnabled('amazon');
  return (
    <AppShell>
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl">Review your buy plan</h1>
            <p className="mt-2 text-sm text-fg-secondary">
              Adjust quantities and selling prices, check the numbers, then mark what you ordered.
              Nothing is purchased automatically.
            </p>
          </div>
          <a href="/app/research" className="text-sm text-accent hover:underline">
            Find products in Research →
          </a>
        </header>
        <PlanSection
          planId={planId}
          planQuery={planQuery}
          plans={plansQuery.data ?? []}
          onSelectPlan={setSelectedPlanId}
          onPlanDeleted={() => setSelectedPlanId(null)}
          onNewPlan={async () => {
            setError(null);
            try {
              const plan = await createPlan.mutateAsync({});
              setSelectedPlanId(plan.id);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Could not create plan.');
            }
          }}
          creatingPlan={createPlan.isPending}
          planActionError={error}
          amazonEnabled={amazonEnabled}
        />
      </main>
    </AppShell>
  );
}

/** Existing discovery controls, mounted only when Research tools is expanded. */
export function PlanningDiscoverySection() {
  const amazonEnabled = useModuleEnabled('amazon');
  const recsQuery = useOrderPlanRecommendations(amazonEnabled);
  // Unfiltered (all statuses, newest first per the API) so a plan that was
  // just marked ordered/archived keeps showing read-only after a reload —
  // draft-only would make it vanish behind "No plan yet." the moment it
  // left draft status, even though it's still the plan the VA is looking at.
  const plansQuery = useOrderPlans();

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const effectivePlanId = selectedPlanId ?? plansQuery.data?.[0]?.id ?? null;
  const planQuery = useOrderPlan(effectivePlanId);

  const qc = useQueryClient();
  const createPlan = useCreateOrderPlan();
  const [addingSku, setAddingSku] = useState<string | null>(null);
  const [addFeedback, setAddFeedback] = useState<string | null>(null);

  // Serializes concurrent callers of ensureDraftPlanId (e.g. two "Add to
  // plan" clicks in quick succession before the first create resolves and
  // plansQuery/planQuery reflect it) onto ONE in-flight create, instead of
  // each racing its own POST /plans. Belt and braces, not a correctness
  // requirement — several drafts may coexist per tenant since 2026-09-28
  // (migration 0115), so a concurrent create just makes another draft
  // rather than colliding with anything.
  const ensureDraftPlanIdRef = useRef<Promise<string> | null>(null);

  /** The plan a new line should land on: the currently-viewed plan if it's
   *  still a draft, else the tenant's MOST RECENTLY UPDATED other open
   *  draft if one exists, else a freshly-created one. Used by both "Add to
   *  plan" and "New plan". */
  async function ensureDraftPlanId(): Promise<string> {
    if (planQuery.data && planQuery.data.status === 'draft') return planQuery.data.id;
    const existingDraft = plansQuery.data
      ?.filter((p) => p.status === 'draft')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
    if (existingDraft) {
      setSelectedPlanId(existingDraft.id);
      return existingDraft.id;
    }
    if (ensureDraftPlanIdRef.current) return ensureDraftPlanIdRef.current;
    const inFlight = createPlan
      .mutateAsync({})
      .then((created) => {
        setSelectedPlanId(created.id);
        return created.id;
      })
      .finally(() => {
        ensureDraftPlanIdRef.current = null;
      });
    ensureDraftPlanIdRef.current = inFlight;
    return inFlight;
  }

  async function handleAddToPlan(rec: OrderPlanRecommendation) {
    setAddingSku(rec.sellerSku);
    setAddFeedback(null);
    try {
      const planId = await ensureDraftPlanId();
      await createOrderPlanLine(planId, {
        source: 'reorder_rec',
        sellerSku: rec.sellerSku,
        asin: rec.asin ?? undefined,
        description: rec.productName ?? rec.sellerSku,
        qtyPlanned: rec.suggestedQty && rec.suggestedQty > 0 ? rec.suggestedQty : 1,
        expectedUnitCostCents: rec.expectedUnitCostCents ?? undefined,
      });
      qc.invalidateQueries({ queryKey: ['order-plan', planId] });
      qc.invalidateQueries({ queryKey: ['order-plans'] });
      setAddFeedback(`Added ${rec.sellerSku} to the plan.`);
    } catch (err) {
      setAddFeedback(err instanceof ApiCallError ? err.message : 'Could not add to plan.');
    } finally {
      setAddingSku(null);
    }
  }

  return (
    <>
      <BuyingSettingsSection />

      <PlanningAutomationSection />

      <ReorderSection
        amazonEnabled={amazonEnabled}
        recsQuery={recsQuery}
        onAddToPlan={handleAddToPlan}
        addingSku={addingSku}
        feedback={addFeedback}
      />
    </>
  );
}

function planningStatusLabel(status: string) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function PlanningRunSummaryCard({
  run,
  candidates,
}: {
  run: PlanningRun;
  candidates: PlanningCandidate[];
}) {
  const summary = run.summary;
  const overBudgetCents = summary.overBudgetCents ?? 0;
  const overCeilingCents = summary.overCeilingCents ?? 0;
  const counts = run.counts.reduce<Record<string, number>>((acc, item) => {
    acc[item.status] = (acc[item.status] ?? 0) + item.count;
    return acc;
  }, {});
  const visibleCandidates = candidates.filter(
    (candidate) => candidate.status !== 'done' || candidate.decision != null
  );

  return (
    <section
      aria-label="Nightly buying run"
      className="mt-8 rounded-lg border border-border-subtle p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">Nightly buying run</h2>
          <p className="mt-1 text-xs text-fg-tertiary">
            {run.day} ·{' '}
            <Badge
              variant={
                run.status === 'complete'
                  ? 'positive'
                  : run.status === 'failed'
                    ? 'negative'
                    : 'warning'
              }
            >
              {planningStatusLabel(run.status)}
            </Badge>
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-fg-secondary">
          {(['pending', 'done', 'deferred', 'excluded'] as const).map((status) => (
            <span key={status} className="rounded-sm border border-border-subtle px-2 py-1">
              {planningStatusLabel(status)} · {counts[status] ?? 0}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
        <div>
          <p className="text-xs text-fg-secondary">Planned goods</p>
          <p className="mt-1 tabular-nums">{money(summary.totalGoodsCents)}</p>
        </div>
        <div>
          <p className="text-xs text-fg-secondary">Reserved</p>
          <p className="mt-1 tabular-nums">{money(summary.reservedGoodsCents)}</p>
        </div>
        <div>
          <p className="text-xs text-fg-secondary">
            {overBudgetCents > 0 ? 'Over weekly budget' : 'Remaining'}
          </p>
          <p className={`mt-1 tabular-nums${overBudgetCents > 0 ? ' text-warning' : ''}`}>
            {overBudgetCents > 0
              ? money(overBudgetCents)
              : money(summary.remainingGoodsBudgetCents)}
          </p>
        </div>
      </div>

      {/* 2026-09-05: the weekly budget and the inventory ceiling are advisory —
          buying-math.ts selects past both rather than declining profitable lines.
          The run has to say what taking the order costs against those targets. */}
      {(overBudgetCents > 0 || overCeilingCents > 0) && (
        <div className="mt-4 rounded-md border border-warning/30 bg-warning/[0.06] p-3 text-sm">
          <p className="font-medium">Above your targets — nothing was dropped for it</p>
          <ul className="mt-1 space-y-1 text-fg-secondary">
            {overBudgetCents > 0 && (
              <li>
                {money(overBudgetCents)} over the {money(summary.orderedBudgetCents)} weekly order
                budget.
              </li>
            )}
            {overCeilingCents > 0 && (
              <li>
                Takes committed inventory to {money(summary.projectedCommittedCents)} —{' '}
                {money(overCeilingCents)} above the ceiling. Raising it, or clearing dead stock, is
                the call to make.
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="mt-4 rounded-md bg-bg-subtle p-3 text-sm">
        <p className="font-medium">Run result</p>
        <p className="mt-1 text-fg-secondary">
          {run.impact.proposedLineCount > 0
            ? `Added ${run.impact.proposedLineCount} proposed line${run.impact.proposedLineCount === 1 ? '' : 's'} · ${run.impact.proposedVendorUnits.toLocaleString()} supplier units · ${money(run.impact.proposedGoodsCents)} planned goods`
            : run.status === 'complete'
              ? 'No proposed lines were added.'
              : 'No plan changes yet.'}
        </p>
        {run.planId && (
          <p className="mt-1 text-xs text-fg-tertiary">Saved to the draft buying plan.</p>
        )}
      </div>

      {/* 2026-09-10 per-SKU inventory hold design doc: a run that published
          with some lines individually held reads "N lines held" instead of
          the plan-wide sentence — that sentence is reserved for the whole-
          plan reasons (unverified PO, mixed suppliers, etc.), which still
          set summary.status to 'held', not 'published'. */}
      {summary.status === 'published' && (summary.heldLines ?? 0) > 0 ? (
        <p className="mt-3 text-xs text-fg-secondary">
          {planningHeldLinesLabel(summary.heldLines!)}
        </p>
      ) : (
        summary.reason && <p className="mt-3 text-xs text-fg-secondary">{summary.reason}</p>
      )}

      {visibleCandidates.length > 0 && (
        <div className="mt-4 border-t border-border-subtle pt-3 space-y-2">
          <p className="text-xs uppercase tracking-[0.12em] text-fg-tertiary">Run details</p>
          {visibleCandidates.map((candidate) => (
            <div
              key={candidate.id}
              className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm"
            >
              <span className="min-w-0 truncate text-fg-primary">
                {candidate.description ?? candidate.itemNo ?? candidate.asin ?? candidate.key}
              </span>
              <span className="text-xs text-fg-secondary">
                {candidate.status !== 'done' && `${planningStatusLabel(candidate.status)} · `}
                {candidate.reason ?? candidate.stage ?? 'No additional explanation'}
                {candidate.decision &&
                  'economics' in candidate.decision &&
                  candidate.decision.economics.profitPerListingCents != null && (
                    <>
                      {' '}
                      · {money(candidate.decision.economics.profitPerListingCents)} profit/listing
                    </>
                  )}
                {candidate.decision &&
                  'beyondWeeklyBudget' in candidate.decision &&
                  candidate.decision.beyondWeeklyBudget && (
                    <span className="text-warning"> · beyond weekly budget</span>
                  )}
              </span>
              {candidate.decision && 'economics' in candidate.decision && (
                <PerListingMath proposal={candidate.decision} />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function PlanningAutomationSection() {
  const runQuery = useLatestPlanningRun();
  const candidatesQuery = usePlanningCandidates(runQuery.data?.id ?? null, { limit: 25 });
  const startRun = useStartPlanningRunNow();

  if (runQuery.isPending) {
    return <p className="mt-8 text-sm text-fg-secondary">Loading the latest buying run…</p>;
  }
  if (runQuery.isError) {
    return (
      <div className="mt-8 rounded-lg border border-border-subtle p-4">
        <p role="alert" className="text-sm text-negative">
          Could not load the latest buying run.
        </p>
        <Button
          className="mt-3"
          size="sm"
          loading={startRun.isPending}
          onClick={() => startRun.mutate()}
        >
          {startRun.isPending ? 'Starting…' : 'Run existing-product check'}
        </Button>
      </div>
    );
  }
  if (!runQuery.data) {
    return (
      <div className="mt-8 rounded-lg border border-border-subtle p-4">
        <p className="font-medium">No buying run yet</p>
        <p className="mt-1 text-sm text-fg-secondary">
          Run the existing-product check to see what should be reordered and why.
        </p>
        <Button
          className="mt-3"
          size="sm"
          loading={startRun.isPending}
          onClick={() => startRun.mutate()}
        >
          {startRun.isPending ? 'Starting…' : 'Run existing-product check'}
        </Button>
        {startRun.isError && <p className="mt-2 text-sm text-negative">Could not start the run.</p>}
      </div>
    );
  }
  if (candidatesQuery.isError) {
    return (
      <div className="mt-8">
        <PlanningRunSummaryCard run={runQuery.data} candidates={[]} />
        <div role="alert" className="mt-3 flex flex-wrap items-center gap-3 text-sm text-negative">
          <span>Could not load run details.</span>
          <Button variant="secondary" size="sm" onClick={() => void candidatesQuery.refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  const candidateData = candidatesQuery.data;
  return (
    <>
      <PlanningRunSummaryCard run={runQuery.data} candidates={candidateData?.candidates ?? []} />
      {candidateData && candidateData.total > candidateData.candidates.length && (
        <p className="mt-2 text-xs text-fg-tertiary">
          Showing {candidateData.candidates.length} of {candidateData.total} candidates. More
          details are available in the run history.
        </p>
      )}
    </>
  );
}

const BUYING_SETTING_FIELDS = [
  ['receivingTargetCents', 'Weekly receiving target ($)'],
  ['inventoryCeilingCents', 'Inventory ceiling ($)'],
  ['expectedFillRate', 'Expected fill (%)'],
  ['firstBuyCapCents', 'First-buy cap ($)'],
  ['fixedFreightCents', 'Flat delivery freight ($)'],
  ['minimumOrderGoodsCents', 'Minimum order goods ($)'],
  ['prepPerUnitCents', 'Prep per unit ($)'],
  ['prepWrappedPerUnitCents', 'Prep per unit, wrapped ($)'],
] as const;

// Only the fields the buying-settings editor exposes as money/percent inputs
// — decoupled from the full BuyingSettings shape so a new setting (e.g. the
// sourcing screen's rank cutoff, added 2026-09-06) doesn't force this form to
// grow a field it has no UI for yet.
type EditableBuyingSettingKey = (typeof BUYING_SETTING_FIELDS)[number][0];

export function BuyingSettingsSection() {
  const { data: me } = useMe();
  const query = useBuyingSettings();
  const update = useUpdateBuyingSettings();
  const canEdit = me?.user.role === 'owner' || me?.user.role === 'operator';
  const [inputs, setInputs] = useState<Record<EditableBuyingSettingKey, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const settings = query.data;

  function edit() {
    if (!settings || !canEdit) return;
    setInputs({
      receivingTargetCents: String(settings.receivingTargetCents / 100),
      inventoryCeilingCents: String(settings.inventoryCeilingCents / 100),
      expectedFillRate: String(Number((settings.expectedFillRate * 100).toPrecision(12))),
      firstBuyCapCents: String(settings.firstBuyCapCents / 100),
      fixedFreightCents: String(settings.fixedFreightCents / 100),
      minimumOrderGoodsCents: String(settings.minimumOrderGoodsCents / 100),
      prepPerUnitCents: String(settings.prepPerUnitCents / 100),
      prepWrappedPerUnitCents: String(settings.prepWrappedPerUnitCents / 100),
    });
    setError(null);
  }

  async function save(event: { preventDefault(): void }) {
    event.preventDefault();
    if (!settings || !inputs || !canEdit || update.isPending) return;
    setError(null);
    try {
      await update.mutateAsync(parseBuyingSettingsInputs(inputs, settings));
      setInputs(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save buying settings.');
    }
  }

  return (
    <section
      aria-label="Buying settings"
      className="mt-8 rounded-lg border border-border-subtle p-4"
    >
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-medium">Buying settings</h2>
        {settings && canEdit && !inputs && (
          <Button variant="secondary" size="sm" onClick={edit}>
            Edit
          </Button>
        )}
      </div>
      {query.isPending && (
        <p className="mt-3 text-sm text-fg-secondary">Loading buying settings…</p>
      )}
      {query.isError && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {query.error.message}
        </p>
      )}
      {settings && (
        <>
          <dl className="mt-3 grid gap-4 sm:grid-cols-2">
            {BUYING_SETTING_FIELDS.slice(0, 2).map(([key, label]) => (
              <div key={key}>
                <dt className="text-sm text-fg-secondary">{label.replace(' ($)', '')}</dt>
                <dd className="text-xl font-medium tabular-nums">{money(settings[key])}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-2 text-sm text-fg-secondary">
            {(settings.inventoryCeilingCents / ((settings.receivingTargetCents * 52) / 12)).toFixed(
              1
            )}{' '}
            months of cover at the saved weekly target.
          </p>
          <p className="mt-1 text-xs text-fg-tertiary">
            Saved preferences for buying-plan assembly. Manual plans are not resized or
            budget-checked here.
          </p>
          {inputs && canEdit ? (
            <form onSubmit={save} noValidate className="mt-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {BUYING_SETTING_FIELDS.map(([key, label]) => (
                  <label key={key} className="text-sm text-fg-secondary">
                    {label}
                    <Input
                      name={key}
                      inputMode="decimal"
                      value={inputs[key]}
                      disabled={update.isPending}
                      onChange={(event) => setInputs({ ...inputs, [key]: event.target.value })}
                      className="mt-1"
                    />
                  </label>
                ))}
              </div>
              {error && (
                <p role="alert" className="mt-3 text-sm text-red-600">
                  {error}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <Button type="submit" size="sm" disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={update.isPending}
                  onClick={() => {
                    setInputs(null);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
              {BUYING_SETTING_FIELDS.slice(2).map(([key, label]) => (
                <div key={key}>
                  <dt className="text-fg-secondary">{label.replace(/ \([%$]\)/, '')}</dt>
                  <dd className="tabular-nums">
                    {key === 'expectedFillRate'
                      ? `${Number((settings[key] * 100).toPrecision(12))}%`
                      : money(settings[key])}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────
// Reorder section
// ─────────────────────────────────────────────────────────

function ReorderSection({
  amazonEnabled,
  recsQuery,
  onAddToPlan,
  addingSku,
  feedback,
}: {
  amazonEnabled: boolean;
  recsQuery: ReturnType<typeof useOrderPlanRecommendations>;
  onAddToPlan: (rec: OrderPlanRecommendation) => void;
  addingSku: string | null;
  feedback: string | null;
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ReorderFilterKey>(DEFAULT_REORDER_FILTER);

  const data = recsQuery.data ?? [];
  const counts = useMemo(() => reorderFilterCounts(data), [data]);

  const rows = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const searched = trimmed
      ? data.filter(
          (r) =>
            r.sellerSku.toLowerCase().includes(trimmed) ||
            (r.productName?.toLowerCase().includes(trimmed) ?? false)
        )
      : data;
    return [...filterRecommendations(searched, filter)].sort(compareRecommendations);
  }, [data, query, filter]);

  return (
    <section aria-label="Reorder" className="mt-10">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">Reorder</h2>
        {feedback && <p className="text-xs text-fg-secondary">{feedback}</p>}
      </div>

      {!amazonEnabled ? (
        <div className="mt-3 bg-surface border border-border-subtle rounded-xl px-5 py-6">
          <p className="text-sm text-fg-secondary">
            Reorder recommendations need the Amazon module.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-3 rounded-lg border border-border-subtle bg-surface px-4 py-3 text-sm">
            <p className="font-medium text-fg-primary">Demand signal, then profit check</p>
            <p className="mt-1 text-fg-secondary">
              Reorder now tells you what may run out. Add an item to the plan and choose
              <span className="font-medium"> Check pricing &amp; eligibility</span> to see the
              planned selling price, Amazon fees, unit cost, profit per listing, total profit, and
              estimated sell-through before approving it.
            </p>
          </div>
          <div
            className="mt-3 flex items-center gap-2 flex-wrap"
            role="tablist"
            aria-label="Reorder filter"
          >
            {REORDER_FILTER_PILLS.map((p) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={filter === p.key}
                onClick={() => setFilter(p.key)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
                  filter === p.key
                    ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                    : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
                )}
              >
                {p.label} · {counts[p.key]}
              </button>
            ))}
          </div>

          <div className="mt-3 relative max-w-sm">
            <Search
              className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              aria-hidden="true"
            />
            <Input
              inputSize="sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search SKU or product name..."
              className="pl-9"
            />
          </div>

          <div className="mt-3 bg-surface border border-border-subtle rounded-xl overflow-hidden">
            {recsQuery.isLoading && (
              <table className="w-full text-sm table-fixed">
                <tbody className="divide-y divide-border-subtle">
                  <SkeletonRows
                    rows={5}
                    colWidths={['w-40', 'w-10', 'w-10', 'w-12', 'w-20', 'w-12', 'w-24']}
                    cellClassName="px-4 py-2.5 align-top"
                  />
                </tbody>
              </table>
            )}
            {recsQuery.isError && (
              <p className="text-sm text-negative px-5 py-6">
                Could not load reorder recommendations.
              </p>
            )}
            {!recsQuery.isLoading && !recsQuery.isError && rows.length === 0 && (
              <p className="text-sm text-fg-secondary px-5 py-6">
                {data.length === 0 ? 'No SKUs found — sync Amazon inventory first.' : 'No matches.'}
              </p>
            )}
            {rows.length > 0 && (
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.14em] text-fg-tertiary text-left">
                    <th className="px-4 py-2 font-medium">Product</th>
                    <th className="px-4 py-2 font-medium text-right w-16">Stock</th>
                    <th className="px-4 py-2 font-medium text-right w-16">Inbound</th>
                    <th className="px-4 py-2 font-medium text-right w-20">Est. cover</th>
                    <th className="px-4 py-2 font-medium w-36">Recommendation</th>
                    <th className="px-4 py-2 font-medium text-right w-20">Suggested qty</th>
                    <th className="px-4 py-2 font-medium w-48" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {rows.map((rec) => (
                    <ReorderRow
                      key={rec.sellerSku}
                      rec={rec}
                      onAddToPlan={onAddToPlan}
                      adding={addingSku === rec.sellerSku}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function ReorderRow({
  rec,
  onAddToPlan,
  adding,
}: {
  rec: OrderPlanRecommendation;
  onAddToPlan: (rec: OrderPlanRecommendation) => void;
  adding: boolean;
}) {
  const badge = recommendationBadge(rec.recommendation);
  const excluded = rec.recommendation === 'excluded';
  const label = rec.productName ?? rec.sellerSku;

  return (
    <tr>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <ProductThumb imageUrl={rec.imageUrl} alt={label} size="sm" />
          <div className="min-w-0">
            <p className="text-fg-primary line-clamp-2" title={label}>
              <ProductLink asin={rec.asin}>{label}</ProductLink>
            </p>
            <p className="text-xs text-fg-tertiary font-mono">{rec.sellerSku}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">{rec.stock.toLocaleString('en-US')}</td>
      <td className="px-4 py-2.5 text-right tabular-nums">{rec.inbound.toLocaleString('en-US')}</td>
      <td className="px-4 py-2.5 text-right">
        <p className="tabular-nums">
          {rec.daysOfCover != null ? `${Math.round(rec.daysOfCover)}d` : '—'}
        </p>
        <p className="text-[10px] text-fg-tertiary">estimate from shipment history</p>
      </td>
      <td className="px-4 py-2.5">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        {excluded && rec.noReorderReason && (
          <p className="text-[10px] text-fg-tertiary mt-1 line-clamp-2" title={rec.noReorderReason}>
            {rec.noReorderReason}
          </p>
        )}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {rec.suggestedQty != null ? rec.suggestedQty.toLocaleString('en-US') : '—'}
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="flex items-center justify-end gap-1.5 flex-wrap">
          {excluded ? (
            <AllowReorderingButton sellerSku={rec.sellerSku} />
          ) : (
            <>
              <Button
                size="sm"
                variant="secondary"
                disabled={adding}
                loading={adding}
                onClick={() => onAddToPlan(rec)}
              >
                Add to plan
              </Button>
              <DontReorderButton sellerSku={rec.sellerSku} label={label} />
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Per-row "Don't reorder" toggle: small dialog asking for an optional
 *  reason, then PATCHes the exclusion. Owns its own mutation/dialog state —
 *  the parent table doesn't need to know which row is mid-edit. */
function DontReorderButton({ sellerSku, label }: { sellerSku: string; label: string }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const setExclusion = useSetReorderExclusion();

  async function handleConfirm() {
    setError(null);
    try {
      await setExclusion.mutateAsync({
        sellerSku,
        noReorder: true,
        reason: reason.trim() || undefined,
      });
      setOpen(false);
      setReason('');
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not exclude this SKU.');
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setReason('');
          setError(null);
        }
      }}
    >
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        Don't reorder
      </Button>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Don't reorder {label}?</DialogTitle>
          <DialogDescription>
            This SKU stops appearing in reorder recommendations until you allow reordering again.
          </DialogDescription>
        </DialogHeader>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.1em] text-fg-tertiary">
            Reason (optional)
          </span>
          <Input
            inputSize="sm"
            className="mt-1"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. discontinued by vendor"
          />
        </label>
        {error && <p className="text-xs text-negative mt-2">{error}</p>}
        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={setExclusion.isPending}
            loading={setExclusion.isPending}
            onClick={handleConfirm}
          >
            Don't reorder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Undo side of the exclusion toggle — no confirmation needed, it's
 *  reversible in one click either direction. */
function AllowReorderingButton({ sellerSku }: { sellerSku: string }) {
  const setExclusion = useSetReorderExclusion();
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setError(null);
    try {
      await setExclusion.mutateAsync({ sellerSku, noReorder: false });
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not allow reordering.');
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        size="sm"
        variant="secondary"
        disabled={setExclusion.isPending}
        loading={setExclusion.isPending}
        onClick={handleClick}
      >
        Allow reordering
      </Button>
      {error && <p className="text-xs text-negative">{error}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Plan section
// ─────────────────────────────────────────────────────────

/**
 * Plan picker (design item 1) — a compact, always-expanded strip of recent
 * plans (no dropdown/portal: renderToStaticMarkup can't render Radix's
 * portal-based Select, and a plain list of buttons is simpler anyway for a
 * handful of plans) in PlanSection's header. Each entry shows name, status,
 * line count, and created date; the currently-viewed plan is highlighted.
 * Renders nothing for an empty plan list.
 */
export function PlanPicker({
  plans,
  selectedPlanId,
  onSelect,
}: {
  plans: OrderPlan[];
  selectedPlanId: string | null;
  onSelect: (id: string) => void;
}) {
  if (plans.length === 0) return null;
  return (
    <div
      role="listbox"
      aria-label="Recent plans"
      className="flex items-center gap-1.5 flex-wrap max-w-full"
    >
      {plans.slice(0, 8).map((p) => {
        const selected = p.id === selectedPlanId;
        return (
          <button
            key={p.id}
            type="button"
            role="option"
            aria-selected={selected}
            onClick={() => onSelect(p.id)}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs transition-colors duration-fast',
              selected
                ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
            )}
          >
            <span className="max-w-[140px] truncate">{p.name}</span>
            <Badge
              variant={
                p.status === 'draft' ? 'accent' : p.status === 'ordered' ? 'info' : 'neutral'
              }
            >
              {p.status}
            </Badge>
            {p.lineCount != null && (
              <span className="text-fg-tertiary">
                {p.lineCount} {p.lineCount === 1 ? 'item' : 'items'}
              </span>
            )}
            <span className="text-fg-tertiary">{shortDateLabel(p.createdAt)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Plan header sentence (design item 5) — replaces the old bare "planned
 *  $X · approved $Y" fragment with planHeaderSummary's sentence form, next
 *  to the existing status Badge. */
export function PlanStatusSummary({
  status,
  itemCount,
  plannedCents,
  approvedCents,
}: {
  status: OrderPlanStatus;
  itemCount: number;
  plannedCents: number;
  approvedCents: number;
}) {
  return (
    <p className="text-xs text-fg-tertiary mt-1">
      <Badge variant={status === 'draft' ? 'accent' : status === 'ordered' ? 'info' : 'neutral'}>
        {status}
      </Badge>{' '}
      {planHeaderSummary({ itemCount, plannedCents, approvedCents })}
    </p>
  );
}

/** "Back to draft" confirm copy (design item 3, exact design-doc text). */
export const BACK_TO_DRAFT_CONFIRM_COPY =
  'Lines unlock and this stops being the record of what you ordered.';

/** "Unarchive" confirm copy — same shape as "Back to draft"'s, no exact
 *  wording given in the design doc beyond "confirm dialog". */
export const UNARCHIVE_CONFIRM_COPY =
  'This plan becomes the active draft again — edit it or move it forward when ready.';

/**
 * Per-status lifecycle buttons (design item 3): draft → "Mark ordered";
 * ordered → "Archive" + "Back to draft"; archived → "Unarchive". The two new
 * backward transitions carry their confirm copy as a `title` tooltip (in
 * addition to the actual `confirm()` prompt the click handler shows) so the
 * warning is inspectable/testable without simulating a click.
 */
export function PlanLifecycleButtons({
  status,
  pending,
  onMarkOrdered,
  onArchive,
  onBackToDraft,
  onUnarchive,
}: {
  status: OrderPlanStatus;
  pending: boolean;
  onMarkOrdered: () => void;
  onArchive: () => void;
  onBackToDraft: () => void;
  onUnarchive: () => void;
}) {
  if (status === 'draft') {
    return (
      <Button size="sm" disabled={pending} onClick={onMarkOrdered}>
        Mark ordered
      </Button>
    );
  }
  if (status === 'ordered') {
    return (
      <>
        <Button size="sm" variant="secondary" disabled={pending} onClick={onArchive}>
          Archive
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={pending}
          onClick={onBackToDraft}
          title={BACK_TO_DRAFT_CONFIRM_COPY}
        >
          Back to draft
        </Button>
      </>
    );
  }
  return (
    <Button
      size="sm"
      variant="secondary"
      disabled={pending}
      onClick={onUnarchive}
      title={UNARCHIVE_CONFIRM_COPY}
    >
      Unarchive
    </Button>
  );
}

/** Feedback from a completed research response; request errors are handled separately. */
export function completedResearchFeedback(
  line: OrderPlanLine,
  steps: OrderPlanLineResearchStep[]
): string | null {
  if (steps.length === 0 && line.research?.verdictTier === 'unknown') {
    // The card owns unresolved-identity guidance, including UPC/SKU retries.
    // Older successful responses can carry a misleading "add a UPC" note.
    if (!line.asin && !line.research.asin) return null;
    return line.research.note ?? 'Could not check — this line has no ASIN or UPC to look up.';
  }
  return null;
}

export function DeletePlanButton({
  name,
  pending,
  onDelete,
}: {
  name: string;
  pending: boolean;
  onDelete: () => void;
}) {
  return (
    <Button
      size="sm"
      variant="ghost"
      disabled={pending}
      onClick={() => {
        if (
          confirm(
            `Delete "${name}" and all its items? This cannot be undone. Research results and orders will not be deleted.`
          )
        )
          onDelete();
      }}
    >
      Delete plan
    </Button>
  );
}

function PlanSection({
  planId,
  planQuery,
  plans,
  onSelectPlan,
  onPlanDeleted,
  onNewPlan,
  creatingPlan,
  planActionError,
  amazonEnabled,
}: {
  planId: string | null;
  planQuery: ReturnType<typeof useOrderPlan>;
  plans: OrderPlan[];
  onSelectPlan: (id: string) => void;
  onPlanDeleted: () => void;
  onNewPlan: () => void;
  creatingPlan: boolean;
  planActionError: string | null;
  amazonEnabled: boolean;
}) {
  const plan = planQuery.data;
  const lines = plan?.lines ?? [];
  const isDraft = plan?.status === 'draft';
  // Duplicate-ASIN warning (2026-09-17) — recomputed from `lines` on every
  // render; the plan's line list is small enough that this needs no memo.
  const existingAsins = planAsinUsage(lines);

  const qc = useQueryClient();
  const { data: me } = useMe();
  // I5 code-review catch (2026-09-16): PUT /pack-overrides/:asin is
  // owner/operator only (routes/order-planning.ts's requireOwnerOrOperator)
  // — same role gate BuyingSettingsSection already uses for its own
  // owner/operator-only edit.
  const canEditPack = me?.user.role === 'owner' || me?.user.role === 'operator';
  const updatePlan = useUpdateOrderPlan(planId ?? '');
  const createLine = useCreateOrderPlanLine(planId ?? '');
  const updateLine = useUpdateOrderPlanLine(planId ?? '');
  const deleteLine = useDeleteOrderPlanLine(planId ?? '');
  const deletePlan = useDeleteOrderPlan(planId ?? '');
  const researchLine = useResearchOrderPlanLine(planId ?? '');
  const bulkResearch = useBulkResearchPlan(planId ?? '');
  const putPackOverride = usePutPackOverride();
  const putSellableOverride = usePutSellableOverride();
  const deleteSellableOverride = useDeleteSellableOverride();
  const keepaBudgetQuery = useOrderPlanKeepaBudget();
  const keepaTokensLeft = keepaBudgetQuery.data?.keepaTokensLeft ?? null;

  // Manual sellable override (2026-09-17 design) — see SellableOverrideBlock's
  // doc. Only one line's Details dialog is ever open at a time, so a single
  // saving/justSaved id pair (not a per-line map) is enough.
  const [sellableSavingLineId, setSellableSavingLineId] = useState<string | null>(null);
  const [sellableJustSavedLineId, setSellableJustSavedLineId] = useState<string | null>(null);
  const [sellableError, setSellableError] = useState<string | null>(null);

  function handleSellableMark(lineId: string, asin: string, note?: string) {
    setSellableSavingLineId(lineId);
    setSellableJustSavedLineId(null);
    setSellableError(null);
    putSellableOverride.mutate(
      { asin, note },
      {
        onSuccess: () => {
          setSellableSavingLineId(null);
          setSellableJustSavedLineId(lineId);
        },
        onError: (err) => {
          setSellableSavingLineId(null);
          setSellableError(err instanceof Error ? err.message : 'Could not save.');
        },
      }
    );
  }

  function handleSellableClear(lineId: string, asin: string) {
    setSellableSavingLineId(lineId);
    setSellableJustSavedLineId(null);
    setSellableError(null);
    deleteSellableOverride.mutate(
      { asin },
      {
        onSuccess: () => {
          setSellableSavingLineId(null);
          setSellableJustSavedLineId(lineId);
        },
        onError: (err) => {
          setSellableSavingLineId(null);
          setSellableError(err instanceof Error ? err.message : 'Could not clear.');
        },
      }
    );
  }

  const [nameDraft, setNameDraft] = useState('');
  useEffect(() => {
    setNameDraft(plan?.name ?? '');
  }, [plan?.name]);

  // Post-import research kickoff (Task 10, 2026-09-21 design). Import writes
  // via a plain fetch (importOrder), not a mutation hook, so nothing
  // auto-invalidates the plan the lines landed in — handleImported below
  // does that explicitly. The imported plan is often NOT the one currently
  // selected (a brand-new plan, or an existing draft while some other plan
  // is on screen), so the bulk-research kickoff can't fire in the same tick
  // as onSelectPlan — `planId` (this component's own prop) hasn't caught up
  // to the new selection yet, and useBulkResearchPlan's mutationFn closes
  // over it. Instead, stash the outcome and let this effect fire once
  // `planId` (from the parent) and `plan` (this plan's own detail fetch)
  // both catch up to it — at that point bulkResearch is genuinely bound to
  // the right plan. `plan.name` isn't in OrderImportResponse (the created
  // name is server-decided, supplier name + order number), so the "Created
  // plan X" message also has to wait for that same detail fetch.
  const [importPending, setImportPending] = useState<{
    planId: string;
    createdPlan: boolean;
    lineCount: number;
  } | null>(null);
  // Keyed by planId so a manual plan switch (PlanPicker) naturally retires a
  // stale confirmation instead of it following the user to a plan it isn't
  // about.
  const [importMessage, setImportMessage] = useState<{ planId: string; text: string } | null>(null);

  function handleImported(result: OrderImportResponse) {
    qc.invalidateQueries({ queryKey: ['order-plan', result.planId] });
    qc.invalidateQueries({ queryKey: ['order-plans'] });
    setImportPending({
      planId: result.planId,
      createdPlan: result.createdPlan,
      lineCount: result.lineCount,
    });
    onSelectPlan(result.planId);
  }

  useEffect(() => {
    if (!importPending || importPending.planId !== planId || !plan) return;
    setImportMessage({
      planId: importPending.planId,
      text: importPending.createdPlan
        ? `Created plan ${plan.name}`
        : `Added ${importPending.lineCount} item${importPending.lineCount === 1 ? '' : 's'} to your draft plan`,
    });
    bulkResearch.mutate({ force: false });
    setImportPending(null);
    // bulkResearch is a fresh mutation object every render (react-query
    // always uses the latest one's mutationFn/options regardless of which
    // render's closure called .mutate() — this effect doesn't need it as a
    // dep for that reason, only the plan/import-outcome identity below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importPending, planId, plan]);

  const [lineFeedback, setLineFeedback] = useState<Record<string, string>>({});
  // Set, not a single id: overlapping "Research" clicks on different lines
  // must each keep their own spinner rather than the last click silently
  // stealing every row's loading state.
  const [pendingLineIds, setPendingLineIds] = useState<Set<string>>(new Set());
  const [planError, setPlanError] = useState<string | null>(null);
  // pricing-truth arc Task B additions below.
  // Last completed research response's steps, per line — ephemeral (not
  // persisted on the line itself, see ResearchOrderPlanLineResponse's doc),
  // kept only so a freshly-researched row can show its per-step failures
  // inline without waiting for a page reload.
  const [lineSteps, setLineSteps] = useState<Record<string, OrderPlanLineResearchStep[]>>({});
  const [expandedLineIds, setExpandedLineIds] = useState<Set<string>>(new Set());
  // Keepa out-of-tokens countdown (Phase A.1, 2026-08-31
  // planning-page-usability design) — plan-wide (one Keepa bucket), set
  // when ANY line's research click comes back KEEPA_NO_TOKENS. `keepaRetryAt`
  // is the epoch-ms deadline; `keepaRetryTick` exists only to force a
  // re-render once a second while counting down (its value is unused).
  const [keepaRetryAt, setKeepaRetryAt] = useState<number | null>(null);
  const [, setKeepaRetryTick] = useState(0);
  useEffect(() => {
    if (keepaRetryAt === null) return;
    const interval = setInterval(() => {
      if (Date.now() >= keepaRetryAt) {
        setKeepaRetryAt(null);
        qc.invalidateQueries({ queryKey: ['order-plan-keepa-budget'] });
      } else {
        setKeepaRetryTick((t) => t + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [keepaRetryAt, qc]);
  const keepaRetrySeconds =
    keepaRetryAt !== null ? Math.max(0, Math.ceil((keepaRetryAt - Date.now()) / 1000)) : null;
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<PlanLineFilterKey>(DEFAULT_PLAN_LINE_FILTER);

  function toggleExpanded(lineId: string) {
    setExpandedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }

  function setFeedback(lineId: string, message: string | null) {
    setLineFeedback((prev) => {
      const next = { ...prev };
      if (message == null) delete next[lineId];
      else next[lineId] = message;
      return next;
    });
  }

  function setLinePending(lineId: string, pending: boolean) {
    setPendingLineIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(lineId);
      else next.delete(lineId);
      return next;
    });
  }

  async function commitName() {
    if (!plan || !isDraft) return;
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === plan.name) {
      setNameDraft(plan.name);
      return;
    }
    setPlanError(null);
    try {
      await updatePlan.mutateAsync({ name: trimmed });
    } catch (err) {
      setNameDraft(plan.name);
      setPlanError(err instanceof ApiCallError ? err.message : 'Could not rename the plan.');
    }
  }

  async function handleMarkOrdered() {
    if (!plan) return;
    if (!confirm('Lines lock after this — the plan becomes the record of what you ordered.'))
      return;
    setPlanError(null);
    try {
      await updatePlan.mutateAsync({ status: 'ordered' });
    } catch (err) {
      setPlanError(err instanceof ApiCallError ? err.message : 'Could not mark the plan ordered.');
    }
  }

  async function handleArchive() {
    if (!plan) return;
    if (!confirm('Archive this plan?')) return;
    setPlanError(null);
    try {
      await updatePlan.mutateAsync({ status: 'archived' });
    } catch (err) {
      setPlanError(err instanceof ApiCallError ? err.message : 'Could not archive the plan.');
    }
  }

  /** Phase A item 3 escape hatches — both revert to draft, so both can hit
   *  DRAFT_EXISTS (409) when another draft already exists; the server's own
   *  message ("A draft already exists — archive or order it first.") names
   *  the other draft plainly enough that no extra copy is needed here. */
  async function handleBackToDraft() {
    if (!plan) return;
    if (!confirm(BACK_TO_DRAFT_CONFIRM_COPY)) return;
    setPlanError(null);
    try {
      await updatePlan.mutateAsync({ status: 'draft' });
    } catch (err) {
      if (errorCode(err) === 'DRAFT_EXISTS') {
        setPlanError(err instanceof ApiCallError ? err.message : 'A draft already exists.');
      } else {
        setPlanError(
          err instanceof ApiCallError ? err.message : 'Could not move the plan back to draft.'
        );
      }
    }
  }

  async function handleUnarchive() {
    if (!plan) return;
    if (!confirm(UNARCHIVE_CONFIRM_COPY)) return;
    setPlanError(null);
    try {
      await updatePlan.mutateAsync({ status: 'draft' });
    } catch (err) {
      if (errorCode(err) === 'DRAFT_EXISTS') {
        setPlanError(err instanceof ApiCallError ? err.message : 'A draft already exists.');
      } else {
        setPlanError(err instanceof ApiCallError ? err.message : 'Could not unarchive the plan.');
      }
    }
  }

  async function handleUpdateLine(
    lineId: string,
    patch: {
      qtyPlanned?: number;
      expectedUnitCostCents?: number | null;
      intendedPriceCents?: number | null;
    }
  ) {
    setFeedback(lineId, null);
    const prevResearch = lines.find((l) => l.id === lineId)?.research ?? null;
    try {
      const updated = await updateLine.mutateAsync({ id: lineId, ...patch });
      if (researchInvalidatedByPriceChange(prevResearch, updated.research)) {
        setFeedback(lineId, 'Price or cost changed — research again.');
      }
    } catch (err) {
      setFeedback(lineId, err instanceof ApiCallError ? err.message : 'Could not save.');
    }
  }

  // I5 code-review catch (2026-09-16): same per-line feedback pattern as
  // the other edits above; rethrows so EditablePack can reset its input to
  // the server value on failure (see EditablePack's own commit()).
  async function handlePackCommit(lineId: string, asin: string, vendorUnitsPerListing: number) {
    setFeedback(lineId, null);
    try {
      await putPackOverride.mutateAsync({ asin, vendorUnitsPerListing });
    } catch (err) {
      setFeedback(lineId, err instanceof ApiCallError ? err.message : 'Could not save pack.');
      throw err;
    }
  }

  async function handleSetStatus(lineId: string, status: OrderPlanLineStatus) {
    setFeedback(lineId, null);
    try {
      await updateLine.mutateAsync({ id: lineId, status });
    } catch (err) {
      setFeedback(lineId, err instanceof ApiCallError ? err.message : 'Could not save.');
    }
  }

  async function handleRemove(lineId: string) {
    setFeedback(lineId, null);
    try {
      await deleteLine.mutateAsync(lineId);
    } catch (err) {
      setFeedback(lineId, err instanceof ApiCallError ? err.message : 'Could not remove.');
    }
  }

  async function handleResearch(lineId: string) {
    setFeedback(lineId, null);
    setLinePending(lineId, true);
    try {
      const result = await researchLine.mutateAsync(lineId);
      setLineSteps((prev) => ({ ...prev, [lineId]: result.steps }));
      setFeedback(lineId, completedResearchFeedback(result.line, result.steps));
    } catch (err) {
      if (errorCode(err) === 'RESEARCH_STALE') {
        setFeedback(lineId, 'Cost changed — research again.');
      } else if (errorCode(err) === 'KEEPA_NO_TOKENS') {
        const retryInSeconds = retryInSecondsFromError(err) ?? 60;
        setKeepaRetryAt(Date.now() + retryInSeconds * 1000);
        setFeedback(lineId, 'Keepa is out of tokens — refills ~1/min.');
      } else {
        setFeedback(lineId, err instanceof ApiCallError ? err.message : 'Research failed.');
      }
    } finally {
      setLinePending(lineId, false);
    }
  }

  return (
    <section aria-label="The plan" className="mt-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">The plan</h2>
        <div className="flex items-center gap-2">
          <OrderImportModal onImported={handleImported} />
          <PlanPicker plans={plans} selectedPlanId={plan?.id ?? null} onSelect={onSelectPlan} />
        </div>
      </div>
      {(planError ?? planActionError) && (
        <p className="text-xs text-negative mt-1">{planError ?? planActionError}</p>
      )}

      {planQuery.isLoading && (
        <div className="mt-3 bg-surface border border-border-subtle rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border-subtle flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-40 rounded-xs" />
              <Skeleton className="h-3 w-56 rounded-xs" />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Skeleton className="h-7 w-20 rounded-md" />
              <Skeleton className="h-7 w-20 rounded-md" />
            </div>
          </div>
          <table className="w-full text-sm table-fixed">
            <tbody className="divide-y divide-border-subtle">
              <SkeletonRows rows={3} colWidths={['w-40', 'w-16', 'w-16', 'w-20', 'w-24']} />
            </tbody>
          </table>
        </div>
      )}

      {!planQuery.isLoading && !plan && (
        <div className="mt-3 bg-surface border border-border-subtle rounded-xl px-5 py-6 flex items-center justify-between gap-3">
          <p className="text-sm text-fg-secondary">No plan yet.</p>
          <div className="flex items-center gap-2">
            <OrderImportModal onImported={handleImported} />
            <Button size="sm" disabled={creatingPlan} loading={creatingPlan} onClick={onNewPlan}>
              New plan
            </Button>
          </div>
        </div>
      )}

      {plan && (
        <div className="mt-3 bg-surface border border-border-subtle rounded-xl overflow-hidden">
          {/* Header row */}
          <div className="px-5 py-4 border-b border-border-subtle flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              {isDraft ? (
                <Input
                  inputSize="sm"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={commitName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                  className="max-w-xs font-medium"
                />
              ) : (
                <p className="text-sm font-medium text-fg-primary">{plan.name}</p>
              )}
              <p className="mt-2 text-sm text-fg-secondary">
                {[
                  `${plan.status} · ${lines.length} items`,
                  bulkResearch.isPending
                    ? 'researching…'
                    : bulkResearch.data
                      ? `researched ${bulkResearch.data.researched}, reused ${bulkResearch.data.reused}, ${bulkResearch.data.failed} failed`
                      : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {importMessage && importMessage.planId === plan.id && (
                <p className="mt-1 text-xs text-positive">{importMessage.text}</p>
              )}
              {bulkResearch.isError && (
                <p className="mt-1 text-xs text-negative">
                  {bulkResearch.error instanceof ApiCallError
                    ? bulkResearch.error.message
                    : 'Could not re-check margins.'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <DeletePlanButton
                name={plan.name}
                pending={deletePlan.isPending || updatePlan.isPending}
                onDelete={async () => {
                  setPlanError(null);
                  try {
                    await deletePlan.mutateAsync();
                    onPlanDeleted();
                  } catch (err) {
                    setPlanError(err instanceof Error ? err.message : 'Could not delete the plan.');
                  }
                }}
              />
              <PlanLifecycleButtons
                status={plan.status}
                pending={updatePlan.isPending}
                onMarkOrdered={handleMarkOrdered}
                onArchive={handleArchive}
                onBackToDraft={handleBackToDraft}
                onUnarchive={handleUnarchive}
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={bulkResearch.isPending}
                loading={bulkResearch.isPending}
                onClick={() => bulkResearch.mutate({ force: true })}
                title="Ignore cached evidence and re-check margins for every line."
              >
                Re-check all
              </Button>
              {!isDraft && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={creatingPlan}
                  loading={creatingPlan}
                  onClick={onNewPlan}
                >
                  New plan
                </Button>
              )}
            </div>
          </div>

          <PlanMoneySummary lines={lines} />

          {/* Lines */}
          {lines.length === 0 ? (
            <p className="text-sm text-fg-secondary px-5 py-6">
              No items yet — choose products in Research or add an item below.
            </p>
          ) : (
            <PlanLinesTable
              lines={lines}
              isDraft={isDraft}
              query={query}
              onQueryChange={setQuery}
              filter={filter}
              onFilterChange={setFilter}
              lineFeedback={lineFeedback}
              pendingLineIds={pendingLineIds}
              lineSteps={lineSteps}
              expandedLineIds={expandedLineIds}
              keepaTokensLeft={keepaTokensLeft}
              keepaRetrySeconds={keepaRetrySeconds}
              sellableSavingLineId={sellableSavingLineId}
              sellableJustSavedLineId={sellableJustSavedLineId}
              sellableError={sellableError}
              onSellableMark={handleSellableMark}
              onSellableClear={handleSellableClear}
              onToggleExpanded={toggleExpanded}
              onUpdate={handleUpdateLine}
              onPackCommit={handlePackCommit}
              canEditPack={canEditPack}
              onSetStatus={handleSetStatus}
              onRemove={handleRemove}
              onResearch={handleResearch}
            />
          )}

          {/* Manual add */}
          {isDraft && (
            <AddLineForm
              amazonEnabled={amazonEnabled}
              existingAsins={existingAsins}
              onSubmit={async (input) => {
                const created = await createLine.mutateAsync(input);
                // A research-linked pick already has its cost/price/research
                // copied from the saved candidate server-side — firing the
                // usual post-add live re-check on top of that would both
                // spend Keepa/Gemini budget for no reason (the 60s per-line
                // cooldown doesn't protect hours-old saved research from
                // this immediate call) and overwrite it with a fresh live
                // result (bug fix 2026-09-16 review).
                if (!shouldAutoResearchNewLine(input)) return;
                try {
                  await researchLine.mutateAsync(created.id);
                } catch {
                  // Auto-fired research failing (e.g. no Keepa key locally)
                  // shouldn't block the add — the line just shows unverified.
                }
              }}
            />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Responsive plan cards with controlled filters/search. Row-level mutation
 * state (feedback, pending, expanded, last research steps) lives on PlanSection.
 */
export function PlanMoneySummary({ lines }: { lines: OrderPlanLine[] }) {
  const included = lines.filter((l) => l.status !== 'rejected' && !l.holdReason);
  const costs = included
    .map((l) => planLineLeadSummary(l).outCents)
    .filter((n): n is number => n !== null);
  const estimates = included.map(planLineProfitEstimate);
  const profits = estimates.map((e) => e.cents).filter((n): n is number => n !== null);
  const leftoverUnits = estimates.reduce((sum, e) => sum + e.leftoverUnits, 0);
  const missing = included.length - costs.length;
  const marginPct = planSummaryMarginPct(lines);
  return (
    <div className="grid sm:grid-cols-3 gap-5 px-5 py-5 border-b border-border-subtle bg-surface-elev/30">
      <div>
        <p className="text-sm text-fg-secondary">
          Purchase cost{missing > 0 && costs.length > 0 ? ' · partial' : ''}
        </p>
        <p className="text-2xl tabular-nums mt-1">
          {costs.length ? money(costs.reduce((a, b) => a + b, 0)) : '—'}
        </p>
        <p className="text-xs text-fg-secondary mt-1">
          {missing
            ? `${missing} item${missing === 1 ? '' : 's'} missing cost`
            : 'Quantity × unit cost'}
        </p>
      </div>
      <div>
        <p className="text-sm text-fg-secondary">
          Estimated profit
          {profits.length > 0 && profits.length < included.length ? ' · partial' : ''}
        </p>
        <p className="text-2xl tabular-nums mt-1">
          {profits.length ? money(profits.reduce((a, b) => a + b, 0)) : '—'}
        </p>
        {marginPct !== null && (
          <p className="text-xs text-fg-secondary mt-1">{`${percent(marginPct)} margin`}</p>
        )}
        <p className="text-xs text-fg-secondary mt-1">
          {profits.length} of {included.length} items have a total-profit estimate
        </p>
        {leftoverUnits > 0 && (
          <p className="text-xs text-fg-secondary mt-1">
            {leftoverUnits} supplier unit{leftoverUnits === 1 ? '' : 's'} left outside complete
            listing packs
          </p>
        )}
      </div>
      <div>
        <p className="text-sm text-fg-secondary">In this plan</p>
        <p className="text-2xl tabular-nums mt-1">{included.length} items</p>
        <p className="text-xs text-fg-secondary mt-1">
          {lines.length - included.length} rejected or held items excluded from totals
        </p>
      </div>
    </div>
  );
}

export function CompactPlanRow({
  line,
  isDraft,
  canEditPack = true,
  onUpdate,
  onPackCommit,
  onRemove,
  onDetails,
  feedback,
}: {
  line: OrderPlanLine;
  isDraft: boolean;
  /** I5 code-review catch (2026-09-16): PUT /pack-overrides/:asin is
   *  owner/operator only — defaults to true so every OTHER existing
   *  caller/test (this file's own render helpers included) keeps its
   *  pre-existing behavior without threading the prop through. */
  canEditPack?: boolean;
  feedback?: string;
  onUpdate: (patch: {
    qtyPlanned?: number;
    expectedUnitCostCents?: number | null;
    intendedPriceCents?: number | null;
  }) => void;
  /** Pack-quantity design (2026-09-16) — saves through the same pack-
   *  override PUT the Research page's Pack panel uses. Lifted to the
   *  caller (PlanSection) so this row stays a pure, provider-free
   *  component, same as onUpdate/onRemove/onResearch above. Returning a
   *  rejected promise lets EditablePack reset its input to the server
   *  value on failure (I5). */
  onPackCommit: (
    lineId: string,
    asin: string,
    vendorUnitsPerListing: number
  ) => void | Promise<void>;
  onRemove: () => void;
  onDetails: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const lead = planLineLeadSummary(line);
  const margin = line.research?.estMarginCents ?? null;
  const marginPct = line.research?.marginPct ?? null;
  const discount = planLineDiscountNeeded(line);
  // Pack-quantity design (2026-09-16): the effective pack (override, if any,
  // else the pack research itself resolved) is already reflected in
  // line.research.unitsPerListing by the server (GET /plans/:id applies
  // applyPackToResearch) — this field just edits it in place. No ASIN means
  // there's nothing for sourcing_pack_overrides to key off. packOverride
  // (I4) is shown directly too, so a saved override never looks lost on a
  // research-null or cost-null line.
  const packAsin = line.asin ?? line.research?.asin ?? null;
  const packValue = line.packOverride ?? line.research?.unitsPerListing ?? null;
  // I8: a stocked SKU's own listing_map pack, surfaced only when it
  // disagrees with what's actually applied — never silently shadowed.
  const stockMappingNote = line.stockMappingUnitsPerListing;
  return (
    <tr className="border-b border-border-subtle align-top hover:bg-surface-elev/30">
      <td className="px-2 py-3 min-w-0">
        <div className="flex gap-2">
          <ProductThumb imageUrl={line.imageUrl} alt={line.description} size="sm" />
          <div className="min-w-0">
            <ProductLink asin={line.asin ?? line.research?.asin ?? null}>
              {line.description}
            </ProductLink>
            <p className="text-xs text-fg-secondary mt-1">
              {line.asin ?? line.sellerSku ?? line.itemNo ?? ''}
            </p>
            <p className="text-xs text-fg-secondary font-mono select-text">UPC {line.upc || '—'}</p>
            {line.status === 'rejected' && (
              <p className="text-xs text-negative">Previously rejected · excluded</p>
            )}
            {line.holdReason && (
              <p className="text-xs text-warning">{planLineHoldBadgeLabel(line)} · excluded</p>
            )}
            {feedback && (
              <p role="alert" className="text-xs text-negative mt-1">
                {feedback}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-2 py-3 tabular-nums">
        <p>
          {line.demand?.salesRank != null
            ? `#${line.demand.salesRank.toLocaleString('en-US')}`
            : '—'}
        </p>
        {line.demand && (
          <p className="text-xs text-fg-secondary mt-1 flex items-center gap-1 flex-wrap">
            {line.demand.monthlySales != null && (
              <span>{line.demand.monthlySales.toLocaleString('en-US')}/mo</span>
            )}
            <Badge variant={velocityChip(line.demand.velocityTier).variant}>
              {velocityChip(line.demand.velocityTier).label}
            </Badge>
          </p>
        )}
      </td>
      <td className="px-2 py-3">
        <label>
          <span className="sr-only">Quantity for {line.description}</span>
          <EditableNumber
            value={line.qtyPlanned}
            disabled={!isDraft}
            onCommit={(qty) => onUpdate({ qtyPlanned: qty })}
          />
        </label>
      </td>
      <td className="px-2 py-3">
        <label>
          <span className="sr-only">Pack for {line.description}</span>
          <EditablePack
            value={packValue}
            disabled={!isDraft || packAsin === null || !canEditPack}
            onCommit={(units) => (packAsin ? onPackCommit(line.id, packAsin, units) : undefined)}
          />
        </label>
        {stockMappingNote !== null && (
          <p className="text-[10px] text-fg-secondary mt-1">Stock mapping: {stockMappingNote}</p>
        )}
      </td>
      <td className="px-2 py-3">
        <label>
          <span className="sr-only">Unit cost for {line.description}</span>
          <EditableMoney
            cents={line.expectedUnitCostCents}
            disabled={!isDraft}
            onCommit={(cents) => onUpdate({ expectedUnitCostCents: cents })}
          />
        </label>
      </td>
      <td className="px-2 py-3">
        <label>
          <span className="sr-only">Selling price for {line.description}</span>
          <EditableIntendedPrice
            cents={line.intendedPriceCents ?? line.currentSellingPriceCents ?? null}
            disabled={!isDraft}
            onCommit={(cents) => onUpdate({ intendedPriceCents: cents })}
          />
        </label>
        {line.intendedPriceCents == null && line.currentSellingPriceCents != null && (
          <p className="text-xs text-fg-secondary mt-1">
            Current Amazon price
            {line.currentPriceCheckedAt
              ? ` · synced ${shortDateLabel(line.currentPriceCheckedAt)}`
              : ''}
          </p>
        )}
      </td>
      <td className="px-2 py-3 tabular-nums">
        <p className={margin !== null && margin < 0 ? 'text-negative' : ''}>
          {marginPct !== null ? `${money(margin)} · ${percent(marginPct)}` : money(margin)}
        </p>
        {margin === null && <p className="text-xs text-fg-secondary mt-1">Needs price check</p>}
      </td>
      <td className="px-2 py-3 tabular-nums">{money(lead.outCents)}</td>
      <td className="px-2 py-3" title={discountViableTitle(discount.discount)}>
        {planLineDiscountNeededLabel(discount)}
      </td>
      <td
        className={cn(
          'px-2 py-3 sticky right-0 z-10 bg-surface',
          // Desktop (xl+) fits the whole table without scrolling now that
          // the columns above are tighter — a permanently stuck column
          // there would just cover content that no longer needs it. Phones
          // keep the sticky behavior; `relative overflow-x-auto` on the
          // wrapper above is still load-bearing for them.
          'xl:static xl:bg-transparent'
        )}
      >
        <Button size="sm" variant="secondary" onClick={onDetails}>
          Details
        </Button>
        {isDraft && (
          <button
            className="block mt-2 text-xs text-fg-secondary hover:text-negative"
            onClick={onRemove}
            aria-label={`Remove ${line.description}`}
          >
            Remove
          </button>
        )}
      </td>
    </tr>
  );
}

export function PlanLinesTable({
  lines,
  isDraft,
  canEditPack = true,
  query,
  onQueryChange,
  filter,
  onFilterChange,
  lineFeedback,
  pendingLineIds,
  lineSteps,
  expandedLineIds,
  keepaTokensLeft,
  keepaRetrySeconds,
  sellableSavingLineId = null,
  sellableJustSavedLineId = null,
  sellableError = null,
  onSellableMark = () => {},
  onSellableClear = () => {},
  onToggleExpanded,
  onUpdate,
  onPackCommit,
  onSetStatus,
  onRemove,
  onResearch,
}: {
  lines: OrderPlanLine[];
  isDraft: boolean;
  /** I5 code-review catch (2026-09-16) — see CompactPlanRow's prop doc. */
  canEditPack?: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  filter: PlanLineFilterKey;
  onFilterChange: (filter: PlanLineFilterKey) => void;
  lineFeedback: Record<string, string>;
  pendingLineIds: Set<string>;
  lineSteps: Record<string, OrderPlanLineResearchStep[]>;
  expandedLineIds: Set<string>;
  /** Plan-wide Keepa budget/countdown — see LineRow's prop doc. */
  keepaTokensLeft: number | null;
  keepaRetrySeconds: number | null;
  /** Manual sellable override (2026-09-17 design) — see
   *  SellableOverrideBlock's doc. Only one line's Details dialog can be
   *  open at a time, so a single (not per-line-map) saving/justSaved id
   *  pair is enough — PlanSection compares it against `detail.id` below. */
  sellableSavingLineId?: string | null;
  sellableJustSavedLineId?: string | null;
  sellableError?: string | null;
  onSellableMark?: (lineId: string, asin: string, note?: string) => void;
  onSellableClear?: (lineId: string, asin: string) => void;
  onToggleExpanded: (lineId: string) => void;
  onUpdate: (
    lineId: string,
    patch: {
      qtyPlanned?: number;
      expectedUnitCostCents?: number | null;
      intendedPriceCents?: number | null;
    }
  ) => void;
  /** Pack-quantity design (2026-09-16) — see CompactPlanRow's prop doc. */
  onPackCommit: (
    lineId: string,
    asin: string,
    vendorUnitsPerListing: number
  ) => void | Promise<void>;
  onSetStatus: (lineId: string, status: OrderPlanLineStatus) => void;
  onRemove: (lineId: string) => void;
  onResearch: (lineId: string) => void;
}) {
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailTrigger = useRef<HTMLButtonElement | null>(null);
  const detail = lines.find((line) => line.id === detailId);
  const counts = useMemo(() => planLineFilterCounts(lines), [lines]);
  const visibleLines = useMemo(() => {
    const searched = searchPlanLines(lines, query);
    return filterPlanLines(searched, filter);
  }, [lines, query, filter]);

  return (
    <div>
      <div className="px-5 pt-4 space-y-3">
        <div
          className="flex items-center gap-2 flex-wrap"
          role="tablist"
          aria-label="Plan line filter"
        >
          {PLAN_LINE_FILTER_PILLS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={filter === p.key}
              onClick={() => onFilterChange(p.key)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
                filter === p.key
                  ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                  : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
              )}
            >
              {p.label} · {counts[p.key]}
            </button>
          ))}
        </div>

        <div className="relative max-w-sm">
          <Search
            className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            aria-hidden="true"
          />
          <Input
            inputSize="sm"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search description, SKU, or ASIN..."
            className="pl-9"
          />
        </div>
      </div>

      <div className="mt-3">
        {visibleLines.length === 0 ? (
          <p className="text-sm text-fg-secondary px-5 py-6">No matches.</p>
        ) : (
          <div className="relative overflow-x-auto">
            {/* 2026-09-16 layout fix: at min-w-[1200px] the Rank / Sales
                column sat past the right edge of the ~975px (1280 viewport)
                to ~1086px (1440/1920) wrapper, underneath the sticky
                Details/Remove column — never visible at all on desktop.
                Rank/Sales moves next to Product (it's product info, read
                first); min-w drops to what the tightened columns actually
                need so the whole table fits without horizontal scroll at
                1280px; the sticky column below only applies under `xl`
                (phones/tablets), where the scroll wrapper is still load-bearing. */}
            <table className="w-full min-w-[900px] text-sm text-left">
              <thead className="bg-surface-elev text-xs text-fg-secondary">
                <tr>
                  {[
                    'Product',
                    'Rank / Sales',
                    'Quantity',
                    'Pack',
                    'Unit cost',
                    'Selling price',
                    'Profit/listing',
                    'Line cost',
                    'Discount needed',
                    '',
                  ].map((label) => (
                    <th
                      key={label}
                      className={cn(
                        'px-2 py-3 font-medium',
                        // Details/Remove column stays reachable while the
                        // rest of the row scrolls under it on phones
                        // (2026-09-16 review, M-6) — same background as its
                        // <td>s below. Desktop (xl+) no longer scrolls at
                        // all, so sticking the column there would just
                        // permanently cover whatever sits under it.
                        label === '' &&
                          'sticky right-0 z-10 bg-surface-elev xl:static xl:bg-transparent'
                      )}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleLines.map((line) => (
                  <CompactPlanRow
                    key={line.id}
                    line={line}
                    isDraft={isDraft}
                    canEditPack={canEditPack}
                    feedback={lineFeedback[line.id]}
                    onDetails={(event) => {
                      detailTrigger.current = event.currentTarget;
                      setDetailId(line.id);
                    }}
                    onUpdate={(patch) => onUpdate(line.id, patch)}
                    onPackCommit={onPackCommit}
                    onRemove={() => onRemove(line.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Dialog
        open={!!detail}
        onOpenChange={(open) => {
          if (!open) setDetailId(null);
        }}
      >
        {detail && (
          <DialogContent
            className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-w-xl overflow-y-auto rounded-none"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              detailTrigger.current?.focus({ preventScroll: true });
            }}
          >
            <DialogHeader>
              <DialogTitle>{detail.description}</DialogTitle>
              <DialogDescription>Price checks, fees and evidence for this item.</DialogDescription>
            </DialogHeader>
            <LineRow
              detailsOnly
              line={detail}
              isDraft={isDraft}
              feedback={lineFeedback[detail.id] ?? null}
              researching={pendingLineIds.has(detail.id)}
              steps={lineSteps[detail.id]}
              expanded={true}
              keepaTokensLeft={keepaTokensLeft}
              keepaRetrySeconds={keepaRetrySeconds}
              canEditSellable={canEditPack}
              sellableSaving={sellableSavingLineId === detail.id}
              sellableJustSaved={sellableJustSavedLineId === detail.id}
              sellableError={sellableError}
              onSellableMark={(note) => {
                const asin = detail.asin ?? detail.research?.asin;
                if (asin) onSellableMark(detail.id, asin, note);
              }}
              onSellableClear={() => {
                const asin = detail.asin ?? detail.research?.asin;
                if (asin) onSellableClear(detail.id, asin);
              }}
              onToggleExpanded={() => {}}
              onUpdate={(patch) => onUpdate(detail.id, patch)}
              onSetStatus={(status) => onSetStatus(detail.id, status)}
              onRemove={() => onRemove(detail.id)}
              onResearch={() => onResearch(detail.id)}
            />
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

/** Identity guidance and the research button's tooltip. */
export const UNRESEARCHABLE_COPY =
  'Add a UPC, ASIN, or SKU first — a free-text item has nothing to look up.';

export const RESEARCH_EXPLAINER_COPY =
  "Pulls the live Amazon price, fees, and competition and verdicts this line's margin at your cost.";

function MarketCheckSummary({
  research,
  expectedUnitCostCents,
}: {
  research: NonNullable<OrderPlanLine['research']>;
  expectedUnitCostCents: number | null;
}) {
  const profit = research.marketCheck
    ? planLineDiscountScenarios({ research, expectedUnitCostCents }).scenarios.find(
        (s) => s.discountPct === 0
      )?.profitPerListingCents
    : undefined;
  return (
    <p className="text-xs text-fg-secondary mt-1 max-w-[200px]">
      {!research.marketCheck
        ? 'Market check unavailable — Check margin again.'
        : profit == null
          ? 'Market at list cost: unavailable.'
          : profit === 0
            ? 'Market at list cost: $0.00 estimated profit/listing — break-even.'
            : `Market at list cost: ${money(profit)} estimated profit/listing.`}
    </p>
  );
}

function MarketCheckDetails({
  research,
  expectedUnitCostCents,
}: {
  research: NonNullable<OrderPlanLine['research']>;
  expectedUnitCostCents: number | null;
}) {
  const check = research.marketCheck;
  const marketSource = research.marketSource;
  const supplierSource = research.supplierListSource;
  const amount = (cents: number | null | undefined) => (cents == null ? 'Unknown' : money(cents));
  // 2026-09-16 "give me the numbers" fix: the four discount buckets are
  // derived client-side (planLineDiscountScenarios) so this table always
  // has real numbers whenever price/fee/prep/units are known — a supplier
  // price-file match is no longer required. `basis` tells us whether the
  // list-cost row is priced off the price file or the plan line's own
  // Unit cost.
  const { basis, scenarios } = planLineDiscountScenarios({ research, expectedUnitCostCents });
  return (
    <section className="min-w-0 w-full max-w-xl space-y-2 text-xs text-fg-secondary">
      <h3 className="font-medium text-fg-primary">Market check</h3>
      {check ? (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
            <p>Price/listing: {amount(check.marketPriceCents)}</p>
            <p>Live Amazon fee at this price: {amount(check.liveFeePerListingCents)}</p>
            <p>Prep/listing: {amount(check.prepPerListingCents)}</p>
            <p>Vendor units/listing: {check.unitsPerListing}</p>
          </div>
          {basis === 'your_cost' && <p>List = your unit cost (no supplier price file).</p>}
          {/* This table is priced at the live market check, NOT the row's
              committed anchor price above — a code-review catch (2026-09-16):
              they can legitimately differ (Buy Box vs. this run's live price
              probe), so the heading says which one drives the numbers below. */}
          <p className="font-medium text-fg-primary">
            At market price {amount(check.marketPriceCents)}
          </p>
          <table className="w-full table-fixed text-left tabular-nums">
            <thead className="text-fg-secondary">
              <tr>
                <th scope="col" className="py-1 pr-2 font-medium">
                  Supplier cost
                </th>
                <th scope="col" className="py-1 pr-2 font-medium">
                  Cost/vendor unit
                </th>
                <th scope="col" className="py-1 pr-2 font-medium">
                  Estimated profit/listing
                </th>
                <th scope="col" className="py-1 font-medium">
                  Estimated margin
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Faraz 2026-09-07: KeHE gives 6% today, 15% is the negotiation
                  target — added as a fourth bucket. */}
              {scenarios.map((scenario) => (
                <tr key={scenario.discountPct} className="border-t border-border-subtle">
                  <th scope="row" className="py-1 pr-2 font-normal">
                    {scenario.discountPct === 0 ? 'List' : `List−${scenario.discountPct}%`}
                  </th>
                  <td className="py-1 pr-2">{amount(scenario.vendorUnitCostCents)}</td>
                  <td className="py-1 pr-2">{amount(scenario.profitPerListingCents)}</td>
                  <td className="py-1">{percent(scenario.marginPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>Discounts are unconfirmed.</p>
        </>
      ) : (
        <p>Market check unavailable — Check margin again.</p>
      )}
      <div className="space-y-1 break-words">
        <p>
          Market source:{' '}
          {marketSource
            ? marketSource.basis === 'amazon_live_offers'
              ? 'Amazon live offers'
              : 'Amazon inventory'
            : 'Unknown'}
          {' · Checked: '}
          {marketSource?.checkedAt ? (
            <time dateTime={marketSource.checkedAt}>{shortDateLabel(marketSource.checkedAt)}</time>
          ) : (
            'Unknown'
          )}
        </p>
        <p>
          Supplier list source:{' '}
          {supplierSource ? (
            <a
              href={`/pricing-sheets/${supplierSource.sheetId}`}
              className="text-accent hover:underline"
            >
              Pricing sheet
            </a>
          ) : (
            'Unknown'
          )}
          {' · Parsed: '}
          {supplierSource?.parsedAt ? (
            <time dateTime={supplierSource.parsedAt}>
              {shortDateLabel(supplierSource.parsedAt)}
            </time>
          ) : (
            'Unknown'
          )}
        </p>
        <p>
          Order increment:{' '}
          {supplierSource?.orderIncrement == null
            ? 'Unknown'
            : `${supplierSource.orderIncrement} vendor units`}
        </p>
      </div>
    </section>
  );
}

export function LineRow({
  detailsOnly = false,
  line,
  isDraft,
  feedback,
  researching,
  steps,
  expanded,
  keepaTokensLeft,
  keepaRetrySeconds,
  canEditSellable = true,
  sellableSaving = false,
  sellableJustSaved = false,
  sellableError = null,
  onSellableMark = () => {},
  onSellableClear = () => {},
  onToggleExpanded,
  onUpdate,
  onSetStatus,
  onRemove,
  onResearch,
}: {
  detailsOnly?: boolean;
  line: OrderPlanLine;
  isDraft: boolean;
  feedback: string | null;
  researching: boolean;
  /** Last COMPLETED research response's steps for this line — undefined
   *  until a research click resolves at least once this session (see
   *  PlanSection's lineSteps state doc). */
  steps: OrderPlanLineResearchStep[] | undefined;
  expanded: boolean;
  /** Plan-wide Keepa budget estimate (Phase A.1, 2026-08-31
   *  planning-page-usability design) — same value for every line's row,
   *  sourced from useOrderPlanKeepaBudget. null = unknown/BYOK-unknown. */
  keepaTokensLeft: number | null;
  /** Plan-wide countdown, set when a research click on ANY line just came
   *  back KEEPA_NO_TOKENS — null once it expires or nothing has failed
   *  that way this session. */
  keepaRetrySeconds: number | null;
  /** Manual sellable override (2026-09-17 design) — owner/operator only,
   *  same gate PUT/DELETE /sellable-overrides/:asin enforces server-side.
   *  Defaults to true so every OTHER existing caller/test keeps its
   *  pre-existing behavior without threading the prop through, same
   *  convention as CompactPlanRow's canEditPack. */
  canEditSellable?: boolean;
  /** Lifted mutation state/callbacks (see SellableOverrideBlock's doc) —
   *  all optional with inert defaults so every other existing caller/test
   *  keeps working unchanged. */
  sellableSaving?: boolean;
  sellableJustSaved?: boolean;
  sellableError?: string | null;
  onSellableMark?: (note?: string) => void;
  onSellableClear?: () => void;
  onToggleExpanded: () => void;
  onUpdate: (patch: {
    qtyPlanned?: number;
    expectedUnitCostCents?: number | null;
    intendedPriceCents?: number | null;
  }) => void;
  onSetStatus: (status: OrderPlanLineStatus) => void;
  onRemove: () => void;
  onResearch: () => void;
}) {
  const research = line.research;
  // Free-text line with no product identity: research can never resolve an
  // ASIN, so the check "succeeds" with zero steps and an unknown verdict —
  // founder UAT 2026-09-01 read that as a dead button. Disable with the
  // reason instead of letting the click no-op.
  const resolvedAsin = line.asin ?? research?.asin;
  const unresearchable = !resolvedAsin && !line.upc && !line.sellerSku;
  const identityGuidance = unresearchable
    ? UNRESEARCHABLE_COPY
    : !resolvedAsin
      ? `This ${line.upc ? 'UPC' : 'SKU'} has not been matched to an Amazon listing. Try the lookup again.`
      : null;
  const badge = research ? verdictTierBadge(research.verdictTier) : null;
  const authBadge = authorizationBadge(research?.authorization ?? null);
  const holdLabel = planLineHoldBadgeLabel(line);
  const stepFailures = steps ? researchStepFailures(steps) : [];
  // A KEEPA_NO_TOKENS response's explicit countdown wins when present;
  // otherwise a known-zero-or-negative estimate computes its own countdown
  // proactively — same math, just not waiting for a click to discover it.
  const effectiveRetrySeconds =
    keepaRetrySeconds ??
    (keepaOutOfTokens(keepaTokensLeft) ? keepaSecondsUntilAvailable(keepaTokensLeft!) : null);
  const keepaBlocked = effectiveRetrySeconds !== null;
  const budgetLabel = effectiveRetrySeconds === null ? keepaBudgetLabel(keepaTokensLeft) : null;
  const mathCardInput = planLineToMathCardInput(line);
  const lead = planLineLeadSummary(line);
  // Line-total margin % (2026-09-16 "add % with estimated profit" request) —
  // only meaningful once at least one complete listing pack fits in
  // qtyPlanned; a fractional/zero pack has no ratio to report even though
  // planLineProfitEstimate still reports $0.00 for it.
  const linePack = line.research?.unitsPerListing ?? null;
  const lineCompleteListings =
    linePack !== null && linePack > 0 ? Math.floor(line.qtyPlanned / linePack) : 0;
  const lineMarginPct = lineCompleteListings > 0 ? (line.research?.marginPct ?? null) : null;
  const secondary = [
    line.sellerSku && `SKU ${line.sellerSku}`,
    line.upc && `UPC ${line.upc}`,
    line.itemNo && `item ${line.itemNo}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article
      className={cn(
        'min-w-0 rounded-lg border border-border-subtle text-sm',
        line.status === 'rejected' && 'opacity-60'
      )}
    >
      <div
        className={
          detailsOnly
            ? 'p-4'
            : 'grid grid-cols-1 gap-5 p-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1fr)]'
        }
      >
        {!detailsOnly && (
          <section className="min-w-0 space-y-2">
            <h3 className="text-xs font-medium text-fg-secondary">Product</h3>
            <div className="flex items-start gap-2.5 min-w-0">
              <ProductThumb imageUrl={line.imageUrl} alt={line.description} size="sm" />
              <div className="min-w-0">
                <p className="text-fg-primary whitespace-normal break-words">
                  <ProductLink asin={line.asin ?? line.research?.asin ?? null}>
                    {line.description}
                  </ProductLink>
                </p>
                {secondary && (
                  <p className="text-xs text-fg-tertiary font-mono break-words">{secondary}</p>
                )}
                {holdLabel && (
                  <div className="mt-1 max-w-[220px]">
                    <Badge variant="warning">{holdLabel}</Badge>
                  </div>
                )}
                {research && research.risks.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1 max-w-[160px]">
                    {research.risks.map((r) => (
                      <Badge key={r} variant="warning">
                        {handlingRiskLabel(r)}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
        {!detailsOnly && (
          <section className="min-w-0 space-y-2">
            <h3 className="text-xs font-medium text-fg-secondary">Planned buy</h3>
            <LabeledField label="Qty">
              <EditableNumber
                value={line.qtyPlanned}
                disabled={!isDraft}
                onCommit={(qty) => onUpdate({ qtyPlanned: qty })}
              />
            </LabeledField>
            <LabeledField label="Intended price">
              <EditableIntendedPrice
                cents={line.intendedPriceCents}
                disabled={!isDraft}
                onCommit={(cents) => onUpdate({ intendedPriceCents: cents })}
              />
            </LabeledField>
            <LabeledField label="Unit cost">
              <EditableMoney
                cents={line.expectedUnitCostCents}
                disabled={!isDraft}
                onCommit={(cents) => onUpdate({ expectedUnitCostCents: cents })}
              />
            </LabeledField>
          </section>
        )}
        <section className="min-w-0 space-y-2">
          <h3 className="text-xs font-medium text-fg-secondary">Price checks</h3>
          {/* Lead with the line total (Faraz: the row read as confusing
              because per-unit math came first) — units, money out, and,
              only when a planner freight allocation exists, money back.
              Everything below this is detail, not the headline. */}
          <div className="space-y-0.5">
            <p className="text-fg-primary font-medium tabular-nums">
              {lead.qtyPlanned} unit{lead.qtyPlanned === 1 ? '' : 's'} · {money(lead.outCents)} out
              {lead.backCents !== null && (
                // A line the planner selected is profitable by construction, but
                // qty is editable after the fact — never paint a loss green.
                <span className={lead.backCents > 0 ? 'text-positive' : 'text-negative'}>
                  {' '}
                  · {lead.backCents > 0 ? '+' : ''}
                  {money(lead.backCents)} back
                </span>
              )}
            </p>
            <p className="text-xs text-fg-secondary tabular-nums">{planLinePerUnitLabel(lead)}</p>
            {lead.backCents === null && planLineProfitEstimate(line).cents !== null && (
              <p className="text-xs text-fg-secondary">
                {`Estimated line profit: ${money(planLineProfitEstimate(line).cents)}${
                  lineMarginPct !== null ? ` · ${percent(lineMarginPct)} margin` : ''
                } · complete listing packs only`}
              </p>
            )}
            {lead.backCents === null && planLineProfitEstimate(line).cents === null && (
              <p className="text-[10px] text-fg-tertiary">{LINE_TOTAL_NOT_COMPUTED_NOTE}</p>
            )}
          </div>
          {resolvedAsin && research && (
            <>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant={badge!.variant}>{badge!.label}</Badge>
                {/* Authorization folds into the verdict area instead of a
                    second competing badge in Decision — an all-clear
                    ('authorized') needs no call-out, same convention as
                    the math card's authorizationCardLine. */}
                {authBadge.label !== 'Authorized' && (
                  <Badge variant={authBadge.variant}>{authBadge.label}</Badge>
                )}
              </div>
              <p className="text-xs text-fg-secondary">{researchVerdictSentence(line)}</p>
              <MarketCheckSummary
                research={research}
                expectedUnitCostCents={line.expectedUnitCostCents}
              />
            </>
          )}
          {identityGuidance && <p className="text-xs text-fg-secondary">{identityGuidance}</p>}
          {resolvedAsin && !research && (
            <p className="text-xs text-fg-tertiary">Not checked yet.</p>
          )}
          {!detailsOnly && resolvedAsin && research && (
            <button
              type="button"
              onClick={onToggleExpanded}
              aria-expanded={expanded}
              className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity duration-fast"
            >
              {expanded ? (
                <ChevronDown className="h-3 w-3 text-fg-tertiary" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-3 w-3 text-fg-tertiary" aria-hidden="true" />
              )}
              {expanded ? 'Hide research details' : 'Show research details'}
            </button>
          )}
          {resolvedAsin && research && (
            // Freshness provenance (Task 10, 2026-09-21 design): relative
            // phrasing ("priced 2 days ago") so staleness reads at a glance
            // — the exact checked date is still one hover away via `title`,
            // for anyone who wants it precisely rather than relatively.
            <p
              className="text-[10px] text-fg-tertiary mt-0.5 text-right"
              title={`Checked ${shortDateLabel(research.checkedAt)}`}
            >
              {`priced ${formatRelativeTime(research.checkedAt)}`}
            </p>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="mt-1 -ml-3"
            disabled={researching || keepaBlocked || unresearchable}
            loading={researching}
            onClick={onResearch}
            title={RESEARCH_EXPLAINER_COPY}
            leadingIcon={<RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
          >
            {!resolvedAsin && !unresearchable
              ? 'Try lookup again'
              : research
                ? 'Check margin again'
                : 'Check margin'}
          </Button>
          {budgetLabel && <p className="text-[10px] text-fg-tertiary mt-0.5">{budgetLabel}</p>}
          {effectiveRetrySeconds !== null && (
            <p className="text-[10px] text-warning mt-0.5">
              {keepaRetryCountdownLabel(effectiveRetrySeconds)}
            </p>
          )}
          {researching && (
            <ul className="mt-1 space-y-0.5">
              {researchProgressLabels().map((s) => (
                <li key={s.step} className="text-[10px] text-fg-tertiary">
                  {s.label}
                </li>
              ))}
            </ul>
          )}
          {!researching && stepFailures.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {stepFailures.map((f) => (
                <li key={f.step} className="text-[10px] text-warning">
                  {f.label}
                </li>
              ))}
            </ul>
          )}
        </section>
        {!detailsOnly && (
          <section className="min-w-0 space-y-2">
            <h3 className="text-xs font-medium text-fg-secondary">Decision</h3>
            {isDraft && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {line.status === 'proposed' && (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => onSetStatus('approved')}>
                      Approve
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onSetStatus('rejected')}>
                      Reject
                    </Button>
                  </>
                )}
                {line.status === 'approved' && (
                  <>
                    <Badge variant="positive">Approved</Badge>
                    <Button size="sm" variant="ghost" onClick={() => onSetStatus('rejected')}>
                      Reject
                    </Button>
                  </>
                )}
                {line.status === 'rejected' && (
                  <>
                    <Badge variant="negative">Rejected</Badge>
                    <Button size="sm" variant="ghost" onClick={() => onSetStatus('proposed')}>
                      Restore
                    </Button>
                  </>
                )}
                {/* Delete stays available but visually subordinate to
                  Approve/Reject — same icon-only button, just dimmed until
                  hovered so it doesn't read as a third equal choice. */}
                <button
                  type="button"
                  onClick={onRemove}
                  aria-label="Remove line"
                  className="ml-auto text-fg-tertiary opacity-50 hover:opacity-100 hover:text-negative transition-all duration-fast"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            )}
            {feedback && <p className="text-xs text-negative mt-1 max-w-[180px]">{feedback}</p>}
          </section>
        )}
        {detailsOnly && feedback && (
          <p role="alert" className="text-sm text-negative mt-2">
            {feedback}
          </p>
        )}
        {detailsOnly && (
          <div className="mt-3 pt-3 border-t border-border-subtle">
            <h3 className="text-xs font-medium text-fg-secondary mb-1.5">Sellable</h3>
            <SellableOverrideBlock
              asin={resolvedAsin ?? null}
              sellableOverride={line.sellableOverride}
              canEdit={canEditSellable}
              saving={sellableSaving}
              justSaved={sellableJustSaved}
              error={sellableError}
              onMark={onSellableMark}
              onClear={onSellableClear}
            />
          </div>
        )}
      </div>
      {expanded && resolvedAsin && research && mathCardInput && (
        <div className="border-t border-border-subtle p-4 bg-surface-elev/40">
          <div className="flex flex-wrap items-start gap-4">
            <div className="w-full min-w-0 space-y-2">
              <h3 className="text-xs font-medium text-fg-primary">Committed-price check</h3>
              <MathCard data={buildMathCard(mathCardInput)} variant="unit-flow" />
            </div>
            <MarketCheckDetails
              research={research}
              expectedUnitCostCents={line.expectedUnitCostCents}
            />
            {(research.history.length > 0 || research.note) && (
              <div className="space-y-1.5 pt-1">
                {research.history.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <svg width={80} height={24} className="text-fg-tertiary" aria-hidden="true">
                      <polyline
                        points={sparklinePath(research.history, 80, 24)}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      />
                    </svg>
                    {research.trend && <TrendIndicator trend={research.trend} />}
                  </div>
                )}
                {research.note && (
                  <p className="text-xs text-fg-tertiary max-w-sm">Note: {research.note}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function TrendIndicator({ trend }: { trend: NonNullable<OrderPlanLine['research']>['trend'] }) {
  if (!trend) return null;
  const Icon =
    trend.direction === 'rising'
      ? TrendingUp
      : trend.direction === 'declining'
        ? TrendingDown
        : Minus;
  const color =
    trend.direction === 'rising'
      ? 'text-positive'
      : trend.direction === 'declining'
        ? 'text-negative'
        : 'text-fg-tertiary';
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${color}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {trend.pctChange >= 0 ? '+' : ''}
      {trend.pctChange.toFixed(1)}%
    </span>
  );
}

/** Inline-editable integer qty. Local text state so typing doesn't fight the
 *  server value; commits on blur or Enter, reverts on invalid input. */
function EditableNumber({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled: boolean;
  onCommit: (value: number) => void;
}) {
  const [text, setText] = useState(String(value));
  // A server value can change under the input while it's focused (another
  // tab's edit landing, a background refetch after an unrelated mutation).
  // Only resync from the prop when the user ISN'T mid-edit — otherwise a
  // refetch would silently blow away what they just typed.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setText(String(value));
  }, [value]);

  function commit() {
    const parsed = Math.trunc(Number(text));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setText(String(value));
      return;
    }
    if (parsed !== value) onCommit(parsed);
    else setText(String(value));
  }

  if (disabled) return <span className="tabular-nums">{value.toLocaleString('en-US')}</span>;

  return (
    <input
      type="number"
      min={1}
      step={1}
      value={text}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className="w-16 text-right bg-transparent border border-border-subtle rounded-sm px-1.5 py-0.5 text-sm tabular-nums focus-visible:outline-none focus-visible:border-accent"
    />
  );
}

/** Manual sellable override block (2026-09-17 design, Faraz: "I need a way
 *  to mark a product sellable in planning or research page. Once I check
 *  manually, I should be able to remove the restrictions.") — the line
 *  Details panel's chip + set/clear control, same "Saved" confirmation
 *  convention as ResearchRowPanel's SellableOverrideControl, restyled for
 *  this page's Details layout.
 *
 *  Provider-free by design, same reason onPackCommit is lifted to the
 *  caller rather than EditablePack calling usePutPackOverride itself: this
 *  row (and PlanLinesTable's own renderToStaticMarkup tests) must render
 *  with no QueryClientProvider. The actual usePutSellableOverride/
 *  useDeleteSellableOverride mutations live in PlanSection; onMark/onClear
 *  and the saving/justSaved/error trio are threaded down through
 *  PlanLinesTable.
 *
 *  Owner/operator gating (canEdit) is enforced by the caller; the "Mark
 *  sellable" trigger is disabled (not hidden) when the line has no
 *  resolved ASIN, so the reason is visible rather than the control just
 *  disappearing. */
function SellableOverrideBlock({
  asin,
  sellableOverride,
  canEdit,
  saving,
  justSaved,
  error,
  onMark,
  onClear,
}: {
  asin: string | null;
  sellableOverride: OrderPlanLine['sellableOverride'];
  canEdit: boolean;
  saving: boolean;
  justSaved: boolean;
  error: string | null;
  onMark: (note?: string) => void;
  onClear: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const chip = sellableOverrideChip(sellableOverride);

  if (!canEdit && !chip) return null;

  function save() {
    const trimmed = note.trim();
    onMark(trimmed === '' ? undefined : trimmed);
    setEditing(false);
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        {chip ? (
          <Badge variant="positive" title={chip.title}>
            {chip.label}
          </Badge>
        ) : (
          <span className="text-xs text-fg-tertiary">Not marked sellable</span>
        )}
        {canEdit &&
          (chip ? (
            <Button size="sm" variant="ghost" disabled={saving} onClick={onClear}>
              Clear
            </Button>
          ) : !editing ? (
            <Button size="sm" variant="ghost" disabled={!asin} onClick={() => setEditing(true)}>
              Mark sellable
            </Button>
          ) : null)}
      </div>
      {canEdit && !chip && editing && (
        <div className="space-y-1.5">
          <Input
            aria-label="Sellable override note"
            inputSize="sm"
            placeholder="Optional note"
            disabled={saving}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" disabled={saving} onClick={save}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {saving && <p className="text-[10px] text-fg-tertiary">Saving…</p>}
      {!saving && justSaved && <p className="text-[10px] text-fg-tertiary">Saved</p>}
      {error && <p className="text-[10px] text-negative">{error}</p>}
    </div>
  );
}

/** Inline-editable pack (units per listing), 1-999 — Planning's Pack field
 *  (2026-09-16 pack-quantity design). Saves through the existing pack-
 *  override PUT, same as the Research page's Pack panel, so the value
 *  agrees everywhere the moment it's committed. Same shape as
 *  EditableNumber above, but null (no research yet) renders as "—" rather
 *  than a bogus 0/1. */
function EditablePack({
  value,
  disabled,
  onCommit,
}: {
  value: number | null;
  disabled: boolean;
  onCommit: (value: number) => void | Promise<void>;
}) {
  const toText = (v: number | null) => (v === null ? '' : String(v));
  const [text, setText] = useState(toText(value));
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setText(toText(value));
  }, [value]);

  // I5 code-review catch (2026-09-16): a rejected commit resets the field
  // back to the current server value instead of leaving the user's
  // (unsaved) input stuck on screen with no indication anything failed —
  // PlanSection's handlePackCommit surfaces the actual error via the
  // row's usual feedback line; this is just the input's own recovery.
  async function commit() {
    const parsed = Math.trunc(Number(text));
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 999) {
      setText(toText(value));
      return;
    }
    if (parsed === value) {
      setText(toText(value));
      return;
    }
    try {
      await onCommit(parsed);
    } catch {
      setText(toText(value));
    }
  }

  if (disabled)
    return (
      <span className="tabular-nums">{value === null ? '—' : value.toLocaleString('en-US')}</span>
    );

  return (
    <input
      type="number"
      min={1}
      max={999}
      step={1}
      value={text}
      placeholder="—"
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        focusedRef.current = false;
        // Returned (not fire-and-forget) so a caller driving this in a
        // test can await the reset-on-failure behavior deterministically.
        return commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className="w-16 text-right bg-transparent border border-border-subtle rounded-sm px-1.5 py-0.5 text-sm tabular-nums focus-visible:outline-none focus-visible:border-accent"
    />
  );
}

/** Inline-editable unit cost, entered/displayed in dollars, stored in cents. */
function EditableMoney({
  cents,
  disabled,
  onCommit,
}: {
  cents: number | null;
  disabled: boolean;
  onCommit: (cents: number | null) => void;
}) {
  const toText = (c: number | null) => (c == null ? '' : (c / 100).toFixed(2));
  const [text, setText] = useState(toText(cents));
  // Same focus-guard as EditableNumber — don't clobber an in-progress edit
  // when the `cents` prop changes out from under a focused input.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setText(toText(cents));
  }, [cents]);

  function commit() {
    const result = parseDollarInput(text);
    if (!result.ok) {
      setText(toText(cents));
      return;
    }
    if (result.cents !== cents) onCommit(result.cents);
    else setText(toText(cents));
  }

  if (disabled) return <span className="tabular-nums">{money(cents)}</span>;

  return (
    <input
      type="number"
      min={0}
      step={0.01}
      value={text}
      placeholder="—"
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className="w-20 text-right bg-transparent border border-border-subtle rounded-sm px-1.5 py-0.5 text-sm tabular-nums focus-visible:outline-none focus-visible:border-accent"
    />
  );
}

/** Plan-line intended-price input (pricing-truth arc Task B) — dollar,
 *  edge-converted to intendedPriceCents at commit, PATCHed to the line. Same
 *  focus-guard/commit-on-blur-or-Enter shape as EditableMoney, but validated
 *  through parseIntendedPriceInput (0 is rejected — see that function's
 *  doc), and null renders as "—" rather than the money() zero-dollar format
 *  since "no intended price set" is the common case, not an edge case. */
function EditableIntendedPrice({
  cents,
  disabled,
  onCommit,
}: {
  cents: number | null;
  disabled: boolean;
  onCommit: (cents: number | null) => void;
}) {
  const toText = (c: number | null) => (c == null ? '' : (c / 100).toFixed(2));
  const [text, setText] = useState(toText(cents));
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setText(toText(cents));
  }, [cents]);

  function commit() {
    const result = parseIntendedPriceInput(text);
    if (!result.ok) {
      setText(toText(cents));
      return;
    }
    if (result.cents !== cents) onCommit(result.cents);
    else setText(toText(cents));
  }

  if (disabled) return <span className="tabular-nums">{cents == null ? '—' : money(cents)}</span>;

  return (
    <input
      type="number"
      min={0.01}
      step={0.01}
      value={text}
      placeholder="—"
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className="w-20 text-right bg-transparent border border-border-subtle rounded-sm px-1.5 py-0.5 text-sm tabular-nums focus-visible:outline-none focus-visible:border-accent"
    />
  );
}

/**
 * Manual-add product search results (design item 4) — presentational,
 * data-props-only so it stays server-renderable on its own. No `imageUrl`
 * on ProductSearchResult (amazon_inventory carries none for the search
 * route — see its schema doc), so ProductThumb always falls back to its
 * initial-letter tile here; still worth using for a consistent row shape
 * with the rest of the page.
 */
const EMPTY_ASIN_USAGE: Map<string, AsinUsage> = new Map();

export function ProductSearchResultsList({
  query,
  results,
  isLoading,
  onPick,
  existingAsins = EMPTY_ASIN_USAGE,
}: {
  query: string;
  results: ProductSearchResult[];
  isLoading: boolean;
  onPick: (result: ProductSearchResult) => void;
  /** Duplicate-ASIN warning (2026-09-17 — Faraz: "planning page allows to
   *  add duplicate ASINs. It should warn that it is already added in the
   *  plan"). Keyed by uppercased ASIN, from planAsinUsage(plan.lines) —
   *  see lookupAsinUsage's doc. Optional/defaulted so existing callers that
   *  don't track a plan (or don't care) keep compiling unchanged. */
  existingAsins?: Map<string, AsinUsage>;
}) {
  const trimmed = query.trim();
  return (
    <div>
      {isLoading && (
        <div className="mt-2 space-y-1.5">
          <p className="sr-only">Searching…</p>
          <SkeletonLine className="h-7 rounded-sm" />
          <SkeletonLine className="h-7 rounded-sm" />
        </div>
      )}
      {!isLoading && trimmed && results.length === 0 && (
        <p className="text-xs text-fg-secondary">No matches — try a SKU, ASIN, or product name.</p>
      )}
      {results.length > 0 && (
        <ul role="listbox" aria-label="Product search results" className="mt-2 space-y-1">
          {results.map((r) => {
            const dup = lookupAsinUsage(existingAsins, r.asin);
            return (
              <li key={r.sellerSku ?? r.researchCandidateId ?? r.asin}>
                <button
                  type="button"
                  onClick={() => onPick(r)}
                  className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-sm border border-border-subtle hover:border-border-default hover:bg-surface-elev/60 transition-colors duration-fast text-left"
                >
                  <ProductThumb
                    imageUrl={null}
                    alt={r.productName ?? r.sellerSku ?? r.asin ?? ''}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1">
                    <p className="text-sm text-fg-primary truncate">
                      {r.productName ?? r.sellerSku ?? r.asin}
                    </p>
                    <p className="text-xs text-fg-tertiary font-mono">{r.sellerSku ?? r.asin}</p>
                  </span>
                  {r.researchCandidateId && (
                    <Badge variant="accent" className="shrink-0">
                      Research
                    </Badge>
                  )}
                  {dup && (
                    <Badge variant="warning" className="shrink-0">
                      Already in plan
                    </Badge>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Search-first step of AddLineForm (design item 4) — the search box plus
 *  results, and the explicit "Add as free-text item" fallback that switches
 *  to the plain field-grid step (AddLineFormFields) unchanged. */
export function AddLineFormSearchStep({
  query,
  onQueryChange,
  results,
  isLoading,
  onPick,
  onFreeText,
  existingAsins = EMPTY_ASIN_USAGE,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  results: ProductSearchResult[];
  isLoading: boolean;
  onPick: (result: ProductSearchResult) => void;
  onFreeText: () => void;
  existingAsins?: Map<string, AsinUsage>;
}) {
  return (
    <div className="px-5 py-4 border-t border-border-subtle space-y-3">
      <LabeledField label="Find a product">
        <div className="relative max-w-sm">
          <Search
            className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            aria-hidden="true"
          />
          <Input
            inputSize="sm"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search SKU, ASIN, or product name..."
            className="pl-9"
          />
        </div>
      </LabeledField>
      <ProductSearchResultsList
        query={query}
        results={results}
        isLoading={isLoading}
        onPick={onPick}
        existingAsins={existingAsins}
      />
      <Button size="sm" variant="ghost" onClick={onFreeText}>
        Add as free-text item
      </Button>
    </div>
  );
}

export interface AddLineFormValues {
  description: string;
  upc: string;
  itemNo: string;
  qty: string;
  cost: string;
  sellerSku: string | null;
  asin: string | null;
  /** Set when the current pick has saved Research (candidate id) — the
   *  server copies its cost/price/research at line-create time, so the
   *  Expected cost input is disabled while this is set (design fix
   *  2026-09-16). */
  researchCandidateId: string | null;
}

const EMPTY_ADD_LINE_FORM_VALUES: AddLineFormValues = {
  description: '',
  upc: '',
  itemNo: '',
  qty: '1',
  cost: '',
  sellerSku: null,
  asin: null,
  researchCandidateId: null,
};

/**
 * The free-text field-grid step of AddLineForm — unchanged fields, now also
 * reachable pre-filled after a product-search pick (design item 4). Values
 * are controlled entirely via props (not internal state) so a picked
 * product's fill can be asserted directly in a static render, without
 * simulating the click that got here.
 */
export function AddLineFormFields({
  values,
  onChange,
  onClearProduct,
  error,
  warning = null,
  submitting,
  onSubmit,
  onCancel,
  existingAsins = EMPTY_ASIN_USAGE,
}: {
  values: AddLineFormValues;
  onChange: (patch: Partial<AddLineFormValues>) => void;
  onClearProduct: () => void;
  error: string | null;
  /** Duplicate-ASIN submit warning (2026-09-17) — AddLineForm's
   *  "click again to add anyway" copy, rendered in the same slot as `error`
   *  but styled as a caution rather than an error. Optional/defaulted so
   *  existing callers that don't drive the two-click flow keep compiling. */
  warning?: string | null;
  submitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  existingAsins?: Map<string, AsinUsage>;
}) {
  // Duplicate-ASIN fields-step notice (2026-09-17) — shown as soon as a
  // picked product's ASIN is already on the plan, independent of the
  // separate two-click submit warning below. Built as one string (not
  // interpolated JSX) so it renders as a single text node.
  const dup = lookupAsinUsage(existingAsins, values.asin);
  const dupNotice = dup
    ? `Already in this plan — ${dup.lines} ${dup.lines === 1 ? 'line' : 'lines'}, qty ${dup.qty}.`
    : null;
  return (
    <div className="px-5 py-4 border-t border-border-subtle space-y-3">
      {(values.sellerSku || values.researchCandidateId) && (
        <div className="flex items-center gap-2 text-xs">
          {values.sellerSku && <Badge variant="accent">SKU {values.sellerSku}</Badge>}
          {values.asin && <span className="font-mono text-fg-tertiary">{values.asin}</span>}
          {values.researchCandidateId && <Badge variant="accent">From Research</Badge>}
          <button
            type="button"
            onClick={onClearProduct}
            className="text-fg-tertiary hover:text-fg-primary underline"
          >
            Change product
          </button>
        </div>
      )}
      {dupNotice && <p className="text-xs text-warning">{dupNotice}</p>}
      <div className="grid gap-2 sm:grid-cols-[2fr,1fr,1fr,90px,110px]">
        <LabeledField label="Description">
          <Input
            inputSize="sm"
            value={values.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Test Sauce 12oz"
          />
        </LabeledField>
        <LabeledField label="UPC">
          <Input
            inputSize="sm"
            value={values.upc}
            onChange={(e) => onChange({ upc: e.target.value })}
          />
        </LabeledField>
        <LabeledField label="Item #">
          <Input
            inputSize="sm"
            value={values.itemNo}
            onChange={(e) => onChange({ itemNo: e.target.value })}
          />
        </LabeledField>
        <LabeledField label="Qty">
          <Input
            inputSize="sm"
            type="number"
            min={1}
            value={values.qty}
            onChange={(e) => onChange({ qty: e.target.value })}
          />
        </LabeledField>
        <LabeledField label="Expected cost">
          <Input
            inputSize="sm"
            type="number"
            min={0}
            step={0.01}
            value={values.cost}
            onChange={(e) => onChange({ cost: e.target.value })}
            placeholder={values.researchCandidateId ? 'From Research' : '$0.00'}
            disabled={Boolean(values.researchCandidateId)}
          />
        </LabeledField>
      </div>
      {error && <p className="text-xs text-negative">{error}</p>}
      {!error && warning && <p className="text-xs text-warning">{warning}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={submitting} loading={submitting} onClick={onSubmit}>
          Add line
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={submitting}>
          Done
        </Button>
      </div>
    </div>
  );
}

/**
 * Manual-add container (design item 4) — search-first when the amazon
 * module is on (AddLineFormSearchStep), landing straight on the free-text
 * grid otherwise (module off means /product-search would 403 anyway).
 * Picking a search result fills sellerSku/asin/description via
 * productSearchResultToLineDefaults and switches straight to the field
 * grid so the VA can set qty/cost and submit; "Add as free-text item" skips
 * search entirely, preserving the pre-Phase-A behavior for products not yet
 * in inventory.
 */
export function AddLineForm({
  onSubmit,
  amazonEnabled,
  existingAsins = EMPTY_ASIN_USAGE,
}: {
  onSubmit: (input: {
    source: 'manual';
    description: string;
    sellerSku?: string;
    asin?: string;
    upc?: string;
    itemNo?: string;
    qtyPlanned: number;
    expectedUnitCostCents?: number;
    researchCandidateId?: string;
  }) => Promise<void>;
  amazonEnabled: boolean;
  existingAsins?: Map<string, AsinUsage>;
}) {
  const [step, setStep] = useState<'closed' | 'search' | 'fields'>('closed');
  const [searchQuery, setSearchQuery] = useState('');
  const productSearch = useProductSearch(searchQuery);
  const [values, setValues] = useState<AddLineFormValues>(EMPTY_ADD_LINE_FORM_VALUES);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Duplicate-ASIN submit warning (2026-09-17 design: "warn, don't block" —
  // the first Add-line click on an ASIN already in the plan shows the
  // warning instead of submitting; a second click ("armed") submits for
  // real. Reset on any ASIN/description/product-selection change, and
  // implicitly on a successful add (reset() below clears those same
  // values).
  const [dupWarning, setDupWarning] = useState<string | null>(null);
  const [dupArmed, setDupArmed] = useState(false);

  function clearDupWarning() {
    setDupWarning(null);
    setDupArmed(false);
  }

  function reset() {
    setValues(EMPTY_ADD_LINE_FORM_VALUES);
    setSearchQuery('');
    clearDupWarning();
  }

  function handlePick(result: ProductSearchResult) {
    const defaults = productSearchResultToLineDefaults(result);
    setValues((v) => ({
      ...v,
      description: defaults.description,
      sellerSku: defaults.sellerSku ?? null,
      asin: defaults.asin ?? null,
      researchCandidateId: defaults.researchCandidateId ?? null,
    }));
    clearDupWarning();
    setStep('fields');
  }

  function handleFieldsChange(patch: Partial<AddLineFormValues>) {
    setValues((v) => ({ ...v, ...patch }));
    // Only a description edit reaches AddLineFormFields' onChange with an
    // ASIN/product-selection change already handled by handlePick/
    // handleClearProduct above — see the armed-state reset doc above.
    if ('description' in patch) clearDupWarning();
  }

  function handleClearProduct() {
    setValues((v) => ({ ...v, sellerSku: null, asin: null, researchCandidateId: null }));
    clearDupWarning();
  }

  async function handleSubmit() {
    setError(null);
    const dup = lookupAsinUsage(existingAsins, values.asin);
    if (dup && !dupArmed) {
      setDupArmed(true);
      setDupWarning(
        `Already in this plan (qty ${dup.qty}). Click Add line again to add it anyway.`
      );
      return;
    }
    setDupWarning(null);
    const trimmedDesc = values.description.trim();
    const parsedQty = Math.trunc(Number(values.qty));
    if (!trimmedDesc) {
      setError('Description is required.');
      return;
    }
    if (!Number.isFinite(parsedQty) || parsedQty <= 0) {
      setError('Qty must be a positive number.');
      return;
    }
    let costCents: number | undefined;
    if (values.cost.trim()) {
      const dollars = Number(values.cost.trim());
      if (!Number.isFinite(dollars) || dollars < 0) {
        setError('Expected cost must be a non-negative number.');
        return;
      }
      costCents = Math.round(dollars * 100);
    }
    setSubmitting(true);
    try {
      await onSubmit({
        source: 'manual',
        description: trimmedDesc,
        sellerSku: values.sellerSku ?? undefined,
        asin: values.asin ?? undefined,
        upc: values.upc.trim() || undefined,
        itemNo: values.itemNo.trim() || undefined,
        qtyPlanned: parsedQty,
        expectedUnitCostCents: manualLineCostCentsToSend(values.researchCandidateId, costCents),
        researchCandidateId: values.researchCandidateId ?? undefined,
      });
      reset();
      setStep('closed');
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not add the line.');
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'closed') {
    return (
      <div className="px-5 py-3 border-t border-border-subtle">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setStep(amazonEnabled ? 'search' : 'fields')}
        >
          + Add item
        </Button>
      </div>
    );
  }

  if (step === 'search') {
    return (
      <AddLineFormSearchStep
        query={searchQuery}
        onQueryChange={setSearchQuery}
        results={productSearch.data ?? []}
        isLoading={productSearch.isFetching}
        onPick={handlePick}
        onFreeText={() => setStep('fields')}
        existingAsins={existingAsins}
      />
    );
  }

  return (
    <AddLineFormFields
      values={values}
      onChange={handleFieldsChange}
      onClearProduct={handleClearProduct}
      error={error}
      warning={dupWarning}
      existingAsins={existingAsins}
      submitting={submitting}
      onSubmit={handleSubmit}
      onCancel={() => {
        setStep('closed');
        reset();
      }}
    />
  );
}

function LabeledField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.1em] text-fg-tertiary">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ─────────────────────────────────────────────────────────
// Approvals — coming next
// ─────────────────────────────────────────────────────────

function ApprovalsComingCard() {
  return (
    <section aria-label="Approvals" className="mt-10">
      <div className="bg-surface border border-border-subtle rounded-xl p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-fg-primary">Approvals</h3>
          <Badge variant="neutral">Coming next</Badge>
        </div>
        <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">
          Restricted-listing cases — apply, track days waiting, and get nudged the moment approval
          lands so stock doesn't sit idle.
        </p>
      </div>
    </section>
  );
}
